import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Divider,
  FormControlLabel,
  Slider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, addDays, eachDayOfInterval, isWeekend, parseISO, getWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useSkills } from '../../contexts/SkillsContext';
import { useAbsences } from '../../contexts/AbsencesContext';
import { useOnCalls } from '../../contexts/OnCallsContext';
import aiConfigService from '../../services/aiConfigService';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import api from '../../services/api';
import AgentDialogueChat from './AgentDialogueChat';

// Fonction utilitaire pour vérifier les jours fériés
const isHoliday = (date, holidays) => {
  if (!holidays || holidays.length === 0) return false;
  const dateString = format(date, 'yyyy-MM-dd');
  return holidays.some(holiday => holiday.date === dateString);
};

// 🔧 CORRECTION UNIFICATION: Fonction utilitaire pour calculer l'écart-type des primes de manière cohérente
const calculatePrimesStandardDeviation = (assignments, technicianStats, selectedTechnicians) => {
  // Calculer les primes totales pour chaque technicien sélectionné
  const primesByTech = {};
  
  // Initialiser avec les primes existantes
  selectedTechnicians.forEach(techId => {
    const stat = technicianStats[techId];
    primesByTech[techId] = stat ? (stat.currentPrimes || 0) : 0;
  });
  
  // Ajouter les primes des nouvelles affectations
  assignments.forEach(assignment => {
    if (primesByTech.hasOwnProperty(assignment.technicianId)) {
      primesByTech[assignment.technicianId] += assignment.estimatedPrime || 478;
    }
  });
  
  const allPrimes = Object.values(primesByTech);
  if (allPrimes.length <= 1) return 0;
  
  const averagePrimes = allPrimes.reduce((sum, p) => sum + p, 0) / allPrimes.length;
  const variance = allPrimes.reduce((sum, p) => sum + Math.pow(p - averagePrimes, 2), 0) / allPrimes.length;
  
  return Math.sqrt(variance);
};

// 🔧 CORRECTION: Fonction pour convertir l'écart-type des primes en score d'équilibrage
const calculateBalanceScoreFromPrimes = (primesStdDev) => {
  // Plus l'écart-type est faible, meilleur est le score
  // Avec un écart-type de 0€ → score 100
  // Avec un écart-type de 1000€ → score proche de 0
  return Math.max(0, 100 - (primesStdDev / 10));
};

// 🔧 NOUVELLE FONCTION: Calculer l'écart min-max des primes (équité perçue)
const calculatePrimesMinMaxGap = (assignments, technicianStats, selectedTechnicians) => {
  // Calculer les primes totales pour chaque technicien sélectionné
  const primesByTech = {};
  
  // Initialiser avec les primes existantes
  selectedTechnicians.forEach(techId => {
    const stat = technicianStats[techId];
    primesByTech[techId] = stat ? (stat.currentPrimes || 0) : 0;
  });
  
  // Ajouter les primes des nouvelles affectations
  assignments.forEach(assignment => {
    if (primesByTech.hasOwnProperty(assignment.technicianId)) {
      primesByTech[assignment.technicianId] += assignment.estimatedPrime || 478;
    }
  });
  
  const allPrimes = Object.values(primesByTech);
  if (allPrimes.length <= 1) return 0;
  
  const minPrimes = Math.min(...allPrimes);
  const maxPrimes = Math.max(...allPrimes);
  
  return {
    gap: maxPrimes - minPrimes,
    min: minPrimes,
    max: maxPrimes,
    periodsGap: (maxPrimes - minPrimes) / 478 // Écart en nombre d'astreintes
  };
};

// 🔧 NOUVELLE FONCTION: Score d'équité basé sur l'écart min-max (priorité absolue)
const calculateEquityScoreFromMinMaxGap = (primesGap) => {
  // TOLÉRANCE ULTRA-STRICTE pour l'équité perçue
  const estimatedPrime = 478;
  
  if (primesGap <= estimatedPrime * 0.5) {
    // Écart ≤ 0.5 astreinte : PARFAIT
    return 100;
  } else if (primesGap <= estimatedPrime) {
    // Écart ≤ 1 astreinte : EXCELLENT
    return 90 - (primesGap / estimatedPrime) * 10;
  } else if (primesGap <= estimatedPrime * 2) {
    // Écart ≤ 2 astreintes : ACCEPTABLE
    return 70 - ((primesGap - estimatedPrime) / estimatedPrime) * 20;
  } else if (primesGap <= estimatedPrime * 3) {
    // Écart ≤ 3 astreintes : PROBLÉMATIQUE
    return 30 - ((primesGap - estimatedPrime * 2) / estimatedPrime) * 20;
  } else {
    // Écart > 3 astreintes : INACCEPTABLE
    return Math.max(0, 10 - ((primesGap - estimatedPrime * 3) / estimatedPrime) * 10);
  }
};

const AutoPlanningDialog = ({ open, onClose, onPlanningGenerated }) => {
  const { technicians } = useTechnicians();
  const { skills } = useSkills();
  const { absences } = useAbsences();
  const { onCalls, addOnCall } = useOnCalls();
  
  // Référence pour le champ Date de fin
  const endDateRef = useRef(null);
  
  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    selectedSkills: [],
    selectedTechnicians: [],
    maxTechniciansPerPeriod: 3,
    aiProvider: 'proxy-llm', // Sera mis à jour depuis la config globale
    aiModel: 'openai/gpt-4o-mini', // Sera mis à jour depuis la config globale
    aiApiKey: '', // Sera mis à jour depuis la config globale
    aiMultipleSolutions: false, // Sera mis à jour depuis la config globale
    constraints: {
      minRestDays: 15  // 🔧 CORRECTION: 15 jours de repos minimum (conforme aux contraintes métier)
    }
  });
  
  const [data, setData] = useState({
    technicians: [],
    skills: [],
    absences: [],
    existingOnCalls: [],
    holidays: []
  });
  
  const [loading, setLoading] = useState(false);
  const [planning, setPlanning] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('setup'); // setup, generating, preview, completed
  const [progressInfo, setProgressInfo] = useState({
    currentStep: 0,
    steps: [],
    currentIteration: 0,
    maxIterations: 0,
    currentMessage: '',
    startTime: null
  });

  useEffect(() => {
    if (open) {
      loadData();
      resetForm();
      loadAIConfig();
    }
  }, [open]);

  // Charger la configuration IA globale
  const loadAIConfig = async () => {
    try {
      const aiConfig = await aiConfigService.loadConfig();
      setFormData(prev => ({
        ...prev,
        aiProvider: aiConfig.planningStrategy || aiConfig.provider || 'multi-agents', // Utiliser planningStrategy en priorité
        aiModel: aiConfig.model,
        aiApiKey: aiConfig.apiKey,
        aiMultipleSolutions: aiConfig.enableMultipleSolutions
      }));
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration IA:', error);
    }
  };

  const loadData = async () => {
    try {
      console.log('=== DÉBUT CHARGEMENT DONNÉES ===');
      console.log('📊 État des contextes :');
      console.log('- Skills reçues du context:', skills?.length || 0, skills);
      console.log('- Technicians reçus du context:', technicians?.length || 0, technicians);
      console.log('- Absences reçues du context:', absences?.length || 0, absences);
      console.log('- OnCalls reçues du context:', onCalls?.length || 0, onCalls);

      if (!skills || skills.length === 0) {
        console.warn('⚠️ PROBLÈME: Aucune compétence trouvée dans le context!');
      }
      
      if (!technicians || technicians.length === 0) {
        console.warn('⚠️ PROBLÈME: Aucun technicien trouvé dans le context!');
      }

      const sortedSkills = skills ? [...skills].sort((a, b) => a.name.localeCompare(b.name)) : [];
      const filteredTechnicians = technicians ? technicians.filter(t => t.isActive !== false) : [];
      
      console.log('📋 Données traitées :');
      console.log('- Compétences triées:', sortedSkills.map(s => ({ id: s.id, name: s.name })));
      console.log('- Techniciens filtrés:', filteredTechnicians.map(t => ({ 
        id: t.id, 
        name: `${t.firstName} ${t.lastName}`, 
        isActive: t.isActive,
        skills: t.skills 
      })));

      const newData = {
        technicians: filteredTechnicians,
        skills: sortedSkills,
        absences: absences || [],
        existingOnCalls: onCalls || [],
        holidays: [] // Temporairement vide
      };

      console.log('💾 Données finales à sauvegarder:', newData);
      setData(newData);
      console.log('✅ Données chargées avec succès');

    } catch (error) {
      console.error('❌ Erreur lors du chargement des données:', error);
      setError('Erreur lors du chargement des données: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      startDate: null,
      endDate: null,
      selectedSkills: [], // Sera mis à jour après le chargement des données
      selectedTechnicians: [],
      maxTechniciansPerPeriod: 3, // Cohérence avec l'état initial
      aiProvider: 'multi-agents', // ✅ Hybride par défaut (meilleure solution)
      aiModel: 'openai/gpt-4.1', // Modèle IA par défaut pour Proxy LLM
      aiApiKey: '', // Clé API optionnelle
      aiMultipleSolutions: false, // Solutions multiples
      constraints: {
        minRestDays: 15  // 🔧 CORRECTION: 15 jours de repos minimum (conforme aux contraintes métier)
      }
    });
    setPlanning(null);
    setError('');
    setStep('setup');
    setProgressInfo({
      currentStep: 0,
      steps: [],
      currentIteration: 0,
      maxIterations: 0,
      currentMessage: '',
      startTime: null
    });
  };

  // Effet séparé pour pré-sélectionner les compétences après chargement des données
  useEffect(() => {
    if (data.skills.length > 0 && formData.selectedSkills.length === 0) {
      setFormData(prev => ({
        ...prev,
        selectedSkills: data.skills.map(skill => skill.id)
      }));
    }
  }, [data.skills]);

  // Effet séparé pour pré-sélectionner les techniciens après chargement des données
  useEffect(() => {
    if (data.technicians.length > 0 && formData.selectedTechnicians.length === 0) {
      setFormData(prev => ({
        ...prev,
        selectedTechnicians: data.technicians.map(tech => tech.id)
      }));
    }
  }, [data.technicians]);

  // Effet pour vérifier la validité du modèle IA selon le fournisseur
  useEffect(() => {
    const openaiModels = ['gpt-4o-mini', 'gpt-4o', 'gpt-4', 'gpt-3.5-turbo'];
    const proxyLlmModels = [
      // 🆕 NOUVEAUX MODÈLES (Décembre 2024) - Les plus puissants pour la planification
      'vertex_ai/claude-opus-4-5', 'vertex_ai/claude-sonnet-4-5', 'openai/gpt-5.1', 'openai/gpt-5.1-codex',
      // OpenAI Models
      'openai/gpt-4o-mini', 'openai/gpt-4o', 'openai/gpt-4.1', 'openai/o1-mini', 'openai/o1-preview', 'openai/gpt-3.5-turbo',
      // Anthropic Models
      'anthropic/claude-3-5-sonnet-20241022', 'anthropic/claude-3-5-haiku-20241022', 'anthropic/claude-3-opus-20240229',
      // Google Vertex AI
      'vertex_ai/claude3.5-sonnet-v2', 'vertex_ai/gemini-2.0-flash', 'vertex_ai/gemini-1.5-flash', 'vertex_ai/mistral-large',
      // Meta Models
      'meta-llama/llama-3.3-70b-instruct', 'meta-llama/llama-3.1-405b-instruct', 'meta-llama/llama-3.1-70b-instruct',
      // Mistral Models
      'mistralai/mistral-large-2411', 'mistralai/pixtral-large-2411', 'mistralai/mistral-small-2409',
      // DeepSeek Models
      'deepseek/deepseek-v3', 'deepseek/deepseek-r1-distill-llama-70b', 'deepseek/deepseek-coder-v2-lite-instruct',
      // Other Models
      'qwen/qwen-2.5-72b-instruct', 'nvidia/llama-3.1-nemotron-70b-instruct', 'google/gemma-2-27b-it'
    ];
    const backtrackingModels = ['human-logic', 'progressive-backtracking', 'constraint-satisfaction'];
    const aiBacktrackingModels = ['ai-backtracking', 'ai-guided-optimization', 'hybrid-intelligence'];
    
    let validModels = [];
    let defaultModel = '';
    
    if (formData.aiProvider === 'progressive-backtracking') {
      validModels = backtrackingModels;
      defaultModel = 'human-logic';
    } else if (formData.aiProvider === 'ai-guided-backtracking') {
      validModels = aiBacktrackingModels;
      defaultModel = 'ai-backtracking';
    } else if (formData.aiProvider === 'openai') {
      validModels = openaiModels;
      defaultModel = 'gpt-4o-mini';
    } else if (formData.aiProvider === 'proxy-llm') {
      validModels = proxyLlmModels;
      defaultModel = 'openai/gpt-4o-mini'; // Bon équilibre vitesse/qualité
    } else if (formData.aiProvider === 'multi-agents') {
      // 🤖💬 Multi-Agents utilise les modèles Proxy LLM (entreprise uniquement)
      validModels = proxyLlmModels;
      defaultModel = 'openai/gpt-4o-mini';
    } else if (formData.aiProvider === 'deterministic') {
      // ⚡ Déterministe n'utilise pas de modèle LLM
      validModels = ['human-reasoning'];
      defaultModel = 'human-reasoning';
    } else {
      // Fallback : utiliser Proxy LLM
      validModels = proxyLlmModels;
      defaultModel = 'openai/gpt-4o-mini';
    }
    
    if (!validModels.includes(formData.aiModel)) {
      console.log('Modèle IA invalide, réinitialisation:', formData.aiModel, 'pour provider:', formData.aiProvider);
      setFormData(prev => ({
        ...prev,
        aiModel: defaultModel
      }));
    }
  }, [formData.aiProvider, formData.aiModel]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Si on change le fournisseur IA, réinitialiser le modèle par défaut
      if (field === 'aiProvider') {
        if (value === 'progressive-backtracking') {
          newData.aiModel = 'human-logic';
        } else if (value === 'openai') {
          newData.aiModel = 'gpt-4o-mini';
        } else if (value === 'proxy-llm') {
          newData.aiModel = 'openai/gpt-4o-mini';
        } else if (value === 'multi-agents') {
          newData.aiModel = 'openai/gpt-4o-mini'; // Multi-agents utilise Proxy LLM
        } else if (value === 'deterministic') {
          newData.aiModel = 'human-reasoning'; // Déterministe
        } else {
          newData.aiModel = 'openai/gpt-4o-mini'; // Fallback
        }
      }
      
      return newData;
    });
    setError('');
  };

  const validateForm = () => {
    if (!formData.startDate || !formData.endDate) {
      setError('Veuillez sélectionner une période');
      return false;
    }
    
    if (formData.startDate >= formData.endDate) {
      setError('La date de fin doit être postérieure à la date de début');
      return false;
    }
    
    if (formData.selectedSkills.length === 0) {
      setError('Veuillez sélectionner au moins une compétence');
      return false;
    }
    
    if (formData.selectedTechnicians.length === 0) {
      setError('Veuillez sélectionner au moins un technicien');
      return false;
    }

    return true;
  };

  const generatePlanning = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setStep('generating');
    setError('');
    
    // 🔧 CORRECTION MAJEURE: Forcer la réinitialisation du planning pour éviter l'affichage de cache
    setPlanning(null);

    // Initialiser les étapes de progression
    const steps = [
      { id: 1, label: 'Initialisation', description: 'Préparation des données et configuration de l\'algorithme', icon: <SettingsIcon /> },
      { id: 2, label: 'Analyse des besoins', description: 'Évaluation des compétences requises et contraintes', icon: <AnalyticsIcon /> },
      { id: 3, label: 'Optimisation intelligente', description: 'Recherche de la meilleure répartition des astreintes', icon: <BuildIcon /> },
      { id: 4, label: 'Validation', description: 'Vérification de la couverture et des contraintes', icon: <CheckCircleIcon /> }
    ];

    setProgressInfo({
      currentStep: 0,
      steps,
      currentIteration: 0,
      maxIterations: 100,
      currentMessage: 'Démarrage de la planification...',
      startTime: Date.now()
    });

    try {
      const result = await calculateOptimalPlanning();
      console.log('🎯 NOUVEAU PLANNING REÇU:', result);
      setPlanning(result);
      setStep('preview');
    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      setError('Erreur lors de la génération du planning: ' + error.message);
      setStep('setup');
    } finally {
      setLoading(false);
    }
  };

  // Fonction utilitaire pour mettre à jour la progression
  const updateProgress = (stepIndex, message, iteration = null) => {
    setProgressInfo(prev => ({
      ...prev,
      currentStep: stepIndex,
      currentMessage: message,
      currentIteration: iteration !== null ? iteration : prev.currentIteration
    }));
  };

  // Fonction utilitaire pour formater le temps écoulé
  const formatElapsedTime = (startTime) => {
    const elapsed = Date.now() - startTime;
    if (elapsed < 1000) {
      return `${elapsed}ms`;
    } else {
      return `${(elapsed / 1000).toFixed(1)}s`;
    }
  };

  const calculatePeriodCoverage = (periodStart, periodEnd, skillCoverageIndex) => {
    // Calculer le nombre de jours dans la période sélectionnée
    const totalDaysInPeriod = Math.ceil((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1;
    
    // Pour la couverture, on ne compte que les périodes d'astreinte, pas les jours individuels
    // Une période d'astreinte = 7 jours (vendredi à jeudi)
    const totalPeriods = Math.ceil(totalDaysInPeriod / 7);
    
    // Le pourcentage requis est directement l'indice de couverture
    const requiredCoveragePercentage = skillCoverageIndex;
    const requiredPeriods = Math.ceil((requiredCoveragePercentage / 100) * totalPeriods);
    
    return {
      totalDaysInPeriod,
      totalPeriods,
      requiredCoveragePercentage,
      requiredPeriods
    };
  };

  const validateSkillCoverage = (assignments) => {
    const coverageIssues = [];
    
    console.log('\n=== VALIDATION COUVERTURE COMPÉTENCES ===');
    console.log(`📊 Total des affectations reçues: ${assignments.length}`);
    
    // Obtenir la liste de toutes les périodes planifiées
    const allPeriods = getPlanningPeriods();
    
    // 🔧 CORRECTION MAJEURE: Grouper les affectations par période pour calculer la couverture correctement
    // L'IA génère des équipes (plusieurs techniciens par période), pas des affectations individuelles
    const assignmentsByPeriod = new Map();
    
    console.log('\n🔍 === DEBUG CONVERSION COMPÉTENCES ===');
    
    assignments.forEach((assignment, index) => {
      console.log(`Affectation ${index + 1}: ${assignment.technicianName}`);
      console.log(`  🎯 Skills: [${(assignment.skills || []).join(', ')}]`);
      console.log(`  📅 Date: ${format(assignment.startDate, 'dd/MM/yyyy', { locale: fr })}`);
      
      const periodKey = assignment.startDate.getTime();
      if (!assignmentsByPeriod.has(periodKey)) {
        assignmentsByPeriod.set(periodKey, {
          startDate: assignment.startDate,
          endDate: assignment.endDate,
          technicians: [],
          allSkills: new Set(),
          allSkillNames: new Set() // 🔧 AJOUT: Aussi garder les noms des compétences
        });
      }
      
      const periodData = assignmentsByPeriod.get(periodKey);
      periodData.technicians.push(assignment);
      
      // 🔧 CORRECTION: Ajouter les compétences en tant qu'IDs ET noms
      if (assignment.skills && Array.isArray(assignment.skills)) {
        assignment.skills.forEach(skill => {
          periodData.allSkills.add(skill);
          periodData.allSkillNames.add(skill);
          
          // Essayer de trouver l'ID correspondant au nom de compétence
          const skillObj = data.skills.find(s => s.name === skill);
          if (skillObj) {
            periodData.allSkills.add(skillObj.id);
          }
        });
      }
    });
    
    console.log(`📊 Périodes uniques détectées: ${assignmentsByPeriod.size}/${allPeriods.length}`);
    
    // Calculer la couverture pour chaque compétence sélectionnée
    formData.selectedSkills.forEach(skillId => {
      const skill = data.skills.find(s => s.id === skillId);
      if (!skill || skill.coverageIndex === undefined || skill.coverageIndex === null) {
        console.log(`⚠️ Compétence ${skill?.name || skillId} : pas d'indice de couverture défini`);
        return;
      }
      
      console.log(`\n--- Analyse ${skill.name} (indice: ${skill.coverageIndex}%) ---`);
      
      const totalRealPeriods = allPeriods.length;
      const requiredCoveragePercentage = skill.coverageIndex;
      const requiredPeriods = Math.ceil((requiredCoveragePercentage / 100) * totalRealPeriods);
      
      console.log(`Période: ${totalRealPeriods} période(s) d'astreinte réelles`);
      console.log(`Couverture requise: ${requiredCoveragePercentage}% = ${requiredPeriods} période(s)`);
      
      // 🔧 CORRECTION: Compter les périodes uniques qui couvrent cette compétence
      let coveredPeriodsCount = 0;
      const coveredPeriodDates = [];
      const uncoveredPeriodDates = [];
      
      // Vérifier chaque période planifiée
      allPeriods.forEach(period => {
        const periodKey = period.startDate.getTime();
        const periodData = assignmentsByPeriod.get(periodKey);
        const periodDate = format(period.startDate, 'dd/MM', { locale: fr });
        
        if (periodData) {
          console.log(`  📅 Période ${periodDate}: Compétences disponibles:`, Array.from(periodData.allSkills));
          console.log(`     🔍 Recherche de "${skillId}" (ID) ou "${skill.name}" (nom)`);
          
          // 🔧 CORRECTION MAJEURE: Gérer la conversion entre chaînes et nombres pour les IDs de compétences
          const skillIdAsString = String(skillId);
          const skillIdAsNumber = parseInt(skillId);
          
          const hasSkillById = periodData.allSkills.has(skillId) || 
                               periodData.allSkills.has(skillIdAsString) || 
                               periodData.allSkills.has(skillIdAsNumber);
          const hasSkillByName = periodData.allSkills.has(skill.name);
          
          if (hasSkillById || hasSkillByName) {
            coveredPeriodsCount++;
            coveredPeriodDates.push(periodDate);
            console.log(`     ✅ COUVERT (par ${hasSkillById ? 'ID' : 'nom'})`);
          } else {
            uncoveredPeriodDates.push(periodDate);
            console.log(`     ❌ NON COUVERT`);
            console.log(`     🔍 Debug: skillId="${skillId}" (type: ${typeof skillId}), skillIdNum=${skillIdAsNumber} (type: ${typeof skillIdAsNumber})`);
            console.log(`     🔍 Debug: Compétences dans Set:`, Array.from(periodData.allSkills).map(s => `${s} (${typeof s})`));
          }
        } else {
          uncoveredPeriodDates.push(periodDate);
          console.log(`  📅 Période ${periodDate}: Aucune donnée trouvée`);
        }
      });
      
      const actualCoveragePercentage = totalRealPeriods > 0 ? 
        (coveredPeriodsCount / totalRealPeriods) * 100 : 0;
      
      console.log(`🔍 Debug pour ${skill.name}:`);
      console.log(`Total des périodes planifiées: ${totalRealPeriods}`);
      console.log(`Périodes UNIQUES couvertes par cette compétence: ${coveredPeriodsCount}`);
      console.log(`Pourcentage de couverture: ${actualCoveragePercentage.toFixed(1)}%`);
      console.log(`Dates couvertes: [${coveredPeriodDates.join(', ')}]`);
      
      const isUnderCovered = actualCoveragePercentage < skill.coverageIndex;
      
      if (isUnderCovered) {
        const deficit = requiredPeriods - coveredPeriodsCount;
        console.log(`❌ Sous-couverture détectée! Déficit: ${deficit} période(s)`);
        console.log(`   Requis: ${requiredPeriods} | Couvert: ${coveredPeriodsCount} | Manquant: ${deficit}`);
        
        if (uncoveredPeriodDates.length > 0) {
          console.log(`Périodes non couvertes pour ${skill.name}:`);
          uncoveredPeriodDates.slice(0, 5).forEach(date => {
            console.log(`  - ${date}`);
          });
          if (uncoveredPeriodDates.length > 5) {
            console.log(`  ... et ${uncoveredPeriodDates.length - 5} autres`);
          }
        }
        
        coverageIssues.push({
          skillId,
          skill,
          requiredCoverage: skill.coverageIndex,
          actualCoverage: actualCoveragePercentage,
          coveredPeriods: coveredPeriodsCount,
          requiredPeriods: requiredPeriods,
          totalPeriods: totalRealPeriods,
          deficit,
          uncoveredPeriods: uncoveredPeriodDates.map(date => date) // Pour l'affichage dans l'UI
        });
      } else {
        console.log(`✅ Couverture OK: ${coveredPeriodsCount}/${totalRealPeriods} périodes (${actualCoveragePercentage.toFixed(1)}%)`);
      }
    });
    
    console.log(`\nRésultat validation: ${coverageIssues.length} problème(s) de couverture`);
    return coverageIssues;
  };

  // Fonction principale de planification - uniquement IA maintenant
  const calculateOptimalPlanning = async () => {
    console.log('=== DÉBUT PLANIFICATION IA ===');
    
    // 🔧 CORRECTION: Forcer le vide du cache console pour éviter les affichages de cache
    console.clear?.();
    console.log('🔄 === NOUVELLE SESSION DE PLANIFICATION ===');
    
    // Générer un ID de session unique pour cette planification
    const sessionId = `planning-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log(`🆔 Session de planification: ${sessionId}`);

    console.log('🤖 === PLANIFICATION IA ACTIVÉE ===');
    return await calculateAIPlanification(sessionId);
  };

  // Fonction de planification IA
  const calculateAIPlanification = async (sessionId) => {
    console.log('🤖 === DÉBUT PLANIFICATION IA ===');
    const startTime = Date.now();
    
    try {
      // Préparer les données pour l'IA
      const periods = getPlanningPeriods();
      const availableTechnicians = data.technicians.filter(t => 
        formData.selectedTechnicians.includes(t.id)
      );
      const requiredSkills = data.skills.filter(s => 
        formData.selectedSkills.includes(s.id)
      );
      
      console.log(`📊 Données: ${periods.length} périodes, ${availableTechnicians.length} techniciens, ${requiredSkills.length} compétences`);
      
      updateProgress(1, 'Calcul des statistiques annuelles...');

      // Calculer les stats annuelles avant l'appel API pour les transmettre au serveur
      const annualStats = await calculateTechnicianStats();

      updateProgress(1, 'Envoi des données à l\'Agent IA...');
      
      // Appeler l'API du serveur pour la planification IA
      const response = await api.post('/api/ai-planning', {
          sessionId,
          periods: periods.map(p => ({
            id: p.id,
            startDate: p.startDate.toISOString(),
            endDate: p.endDate.toISOString(),
            weekNumber: p.weekNumber
          })),
          technicians: availableTechnicians.map(t => ({
            id: t.id,
            firstName: t.firstName,
            lastName: t.lastName,
            skills: Array.isArray(t.skills) ? t.skills.map(skill => 
              typeof skill === 'object' && skill.skillId ? skill.skillId : skill
            ) : [],
            existingOnCallsCount: annualStats[t.id]?.currentAssignments || 0
          })),
          skills: requiredSkills.map(s => ({
            id: s.id,
            name: s.name,
            required: true
          })),
          absences: data.absences || [],
          constraints: {
            maxTechniciansPerPeriod: formData.maxTechniciansPerPeriod,
            minRestDays: formData.constraints.minRestDays
          },
          aiConfig: {
            provider: formData.aiProvider,
            model: formData.aiModel,
            apiKey: formData.aiApiKey,
            multipleSolutions: formData.aiMultipleSolutions
          }
        });

      updateProgress(2, 'Traitement par l\'Agent IA...');
      const aiResult = response;
      
      updateProgress(3, 'Validation du planning généré...');
      
      // Convertir les résultats de l'IA au format attendu
      const rawAssignments = convertAIResultToAssignments(aiResult, periods);
      
      // CORRECTION : Enrichir avec les données des techniciens
      const enrichedAssignments = rawAssignments.map(assignment => {
        // Le serveur peut renvoyer des IDs numériques OU des IDs tech_X (fallback)
        let realTechnicianId = assignment.technicianId;
        
        // Gérer la conversion tech_X vers les vrais IDs
        if (typeof realTechnicianId === 'string' && realTechnicianId.startsWith('tech_')) {
          // CORRECTION CRITIQUE : Le serveur utilise l'ordre par ID croissant, pas l'ordre de sélection frontend
          const techIndex = parseInt(realTechnicianId.replace('tech_', ''));
          console.log(`🔄 Conversion tech_X: ${assignment.technicianId} → index serveur ${techIndex}`);
          
          // Créer la liste ordonnée comme côté serveur (par ID croissant)
          const serverOrderedTechnicians = [...data.technicians]
            .filter(t => formData.selectedTechnicians.includes(t.id))
            .sort((a, b) => a.id - b.id);
          
          if (techIndex >= 0 && techIndex < serverOrderedTechnicians.length) {
            realTechnicianId = serverOrderedTechnicians[techIndex].id;
            console.log(`✅ tech_${techIndex} → ${serverOrderedTechnicians[techIndex].firstName} ${serverOrderedTechnicians[techIndex].lastName} (ID ${realTechnicianId})`);
          } else {
            console.warn(`⚠️ Index tech_X invalide: ${assignment.technicianId} (index ${techIndex}, max: ${serverOrderedTechnicians.length-1})`);
            console.log('🔍 Ordre serveur reconstitué:', serverOrderedTechnicians.map((t, i) => `tech_${i}: ${t.firstName} ${t.lastName} (ID ${t.id})`));
            return null;
          }
        }
        
        // S'assurer que l'ID est bien numérique
        if (typeof realTechnicianId === 'string') {
          realTechnicianId = parseInt(realTechnicianId);
        }
        
        // Trouver le technicien dans nos données
        const technician = data.technicians.find(t => t.id === realTechnicianId);
        if (!technician) {
          console.warn(`⚠️ Technicien non trouvé: ID ${assignment.technicianId} (converti: ${realTechnicianId})`);
          console.log('🔍 Techniciens disponibles:', data.technicians.map(t => `${t.id}: ${t.firstName} ${t.lastName}`));
          return null; // Ignorer cette affectation
        }
        
        console.log(`✅ Technicien trouvé: ID ${realTechnicianId} → ${technician.firstName} ${technician.lastName}`);
        
        // Normaliser les compétences pour s'assurer qu'elles sont des IDs simples
        const normalizedSkills = (technician.skills || assignment.skills || []).map(skill => 
          typeof skill === 'object' && skill.skillId ? skill.skillId : skill
        );
        
        return {
          ...assignment,
          technicianId: realTechnicianId,
          technician: technician,
          skills: normalizedSkills
        };
      }).filter(Boolean); // Enlever les affectations nulles
      
      console.log(`🔄 Enrichissement: ${rawAssignments.length} → ${enrichedAssignments.length} affectations valides`);
      
      // Réutiliser les stats annuelles calculées avant l'appel API
      const initialTechnicianStats = annualStats;
      const conflicts = validateAssignments(enrichedAssignments);
      const coverageIssues = validateSkillCoverage(enrichedAssignments);
      
      const planningResult = {
        assignments: enrichedAssignments,
        summary: calculatePlanningStats(enrichedAssignments, initialTechnicianStats),
        conflicts,
        coverageIssues,
        strategy: `Planification IA (${formData.aiProvider})`,
        processingTime: {
          totalMs: Date.now() - startTime,
          formattedDuration: formatDuration(Date.now() - startTime)
        },
        metadata: aiResult.metadata || {}
      };
      
      console.log(`✅ Planification IA terminée en ${Date.now() - startTime}ms`);
      return planningResult;
      
    } catch (error) {
      console.error('❌ Erreur planification IA:', error);
      throw new Error(`Erreur planification IA: ${error.message}`);
    }
  };

  // Fonction pour convertir les résultats IA en affectations
  const convertAIResultToAssignments = (aiResult, periods) => {
    const assignments = [];
    
    // 🔧 CORRECTION: La réponse du serveur est dans result.assignments, pas directement assignments
    const resultData = aiResult.result || aiResult;
    
    if (!resultData.assignments || !Array.isArray(resultData.assignments)) {
      console.error('Format de réponse IA invalide:', resultData);
      console.error('Structure complète aiResult:', aiResult);
      return assignments;
    }

    // Créer la liste ordonnée comme côté serveur (par ID croissant)
    const serverOrderedTechnicians = [...data.technicians]
      .filter(t => formData.selectedTechnicians.includes(t.id))
      .sort((a, b) => a.id - b.id);

    console.log('🔄 Conversion des affectations IA en format système...');
    console.log(`📋 Ordre serveur: ${serverOrderedTechnicians.map(t => `${t.firstName} ${t.lastName} (ID:${t.id})`).join(', ')}`);
    
    // 🆕 DEBUG: Analyser la désynchronisation périodes/affectations
    console.log(`\n🔍 === ANALYSE SYNCHRONISATION PÉRIODES/AFFECTATIONS ===`);
    console.log(`📊 Périodes frontend: ${periods.length}`);
    console.log(`📊 Affectations serveur: ${resultData.assignments.length}`);
    
    if (periods.length !== resultData.assignments.length) {
      console.warn(`⚠️ DÉSYNCHRONISATION DÉTECTÉE ! ${periods.length} périodes vs ${resultData.assignments.length} affectations`);
      
      // Afficher les dates des périodes frontend
      console.log('\n📅 Périodes frontend :');
      periods.forEach((period, index) => {
        console.log(`  ${index}: ${format(period.startDate, 'dd/MM/yyyy', { locale: fr })} → ${format(period.endDate, 'dd/MM/yyyy', { locale: fr })}`);
      });
      
      // Afficher les IDs des affectations serveur
      console.log('\n🎯 Affectations serveur :');
      resultData.assignments.forEach((assignment, index) => {
        console.log(`  ${index}: ${assignment.periodId || 'N/A'} (${assignment.technicians?.length || 0} techniciens)`);
      });
    }

    resultData.assignments.forEach((assignment, index) => {
      // 🔧 CORRECTION: Gérer le cas où il y a plus d'affectations que de périodes
      let period = periods[index];
      
      if (!period) {
        console.warn(`⚠️ Période manquante pour l'affectation ${index}, tentative de récupération...`);
        
        // Essayer de matcher par periodId si disponible
        if (assignment.periodId) {
          const periodIndex = parseInt(assignment.periodId.replace('period_', ''));
          if (periodIndex >= 0 && periodIndex < periods.length) {
            period = periods[periodIndex];
            console.log(`✅ Période récupérée via periodId: ${assignment.periodId} → index ${periodIndex}`);
          }
        }
        
        // Si toujours pas de période, essayer de l'extraire des dates de l'affectation
        if (!period && assignment.startDate && assignment.endDate) {
          period = {
            startDate: new Date(assignment.startDate),
            endDate: new Date(assignment.endDate),
            weekNumber: getWeek(new Date(assignment.startDate)),
            isWeekend: true,
            isHoliday: false,
            id: assignment.periodId || `period_${index}`
          };
          console.log(`✅ Période créée à partir des dates d'affectation: ${format(period.startDate, 'dd/MM/yyyy', { locale: fr })}`);
        }
        
        if (!period) {
          console.error(`❌ Impossible de récupérer la période pour l'affectation ${index}`);
          return;
        }
      }

      console.log(`\n🔍 Traitement période ${index + 1}/${resultData.assignments.length}:`);
      console.log(`   📅 ${format(period.startDate, 'dd/MM/yyyy', { locale: fr })} → ${format(period.endDate, 'dd/MM/yyyy', { locale: fr })}`);

      if (assignment.technicians && Array.isArray(assignment.technicians)) {
        assignment.technicians.forEach((tech, techIndex) => {
          let realTechnicianId = null;
          let matchingTech = null;

          // Méthode 1: Utiliser directement le nom fourni par le serveur (PRIORITAIRE)
          if (tech.technicianName) {
            // 🔧 CORRECTION MAJEURE: Le serveur fournit déjà les bons noms !
            matchingTech = data.technicians.find(t => 
              `${t.firstName} ${t.lastName}` === tech.technicianName ||
              `${t.firstName} ${t.lastName}`.toUpperCase() === tech.technicianName.toUpperCase() ||
              `${t.lastName} ${t.firstName}` === tech.technicianName ||
              `${t.lastName} ${t.firstName}`.toUpperCase() === tech.technicianName.toUpperCase()
            );
            if (matchingTech && formData.selectedTechnicians.includes(matchingTech.id)) {
              realTechnicianId = matchingTech.id;
              console.log(`   ✅ Mapping nom direct "${tech.technicianName}" → ${matchingTech.firstName} ${matchingTech.lastName} (ID:${realTechnicianId})`);
            }
          }

          // Méthode 2: Mapping par tech_X (fallback uniquement si nom échoue)
          if (!realTechnicianId && tech.technicianId && tech.technicianId.startsWith('tech_')) {
            console.log(`   ⚠️ Fallback vers tech_X pour ${tech.technicianName || 'nom inconnu'}`);
            const techIndex = parseInt(tech.technicianId.replace('tech_', ''));
            if (techIndex >= 0 && techIndex < serverOrderedTechnicians.length) {
              realTechnicianId = serverOrderedTechnicians[techIndex].id;
              matchingTech = serverOrderedTechnicians[techIndex];
              console.log(`   ⚙️ Mapping tech_${techIndex} → ${matchingTech.firstName} ${matchingTech.lastName} (ID:${realTechnicianId})`);
            }
          }

          // Méthode 3: Mapping par ID direct (si fourni)
          if (!realTechnicianId && tech.technicianId && !tech.technicianId.startsWith('tech_')) {
            const directId = parseInt(tech.technicianId);
            matchingTech = data.technicians.find(t => t.id === directId);
            if (matchingTech && formData.selectedTechnicians.includes(matchingTech.id)) {
              realTechnicianId = directId;
              console.log(`   ✅ Mapping ID direct ${directId} → ${matchingTech.firstName} ${matchingTech.lastName}`);
            }
          }

          if (realTechnicianId && matchingTech) {
            // 🔧 CORRECTION: Vérifier la validité des dates avant création
            const startDate = new Date(period.startDate);
            const endDate = new Date(period.endDate);
            
            // Vérifier que les dates sont valides
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
              console.error(`   ❌ Dates invalides pour la période:`, period);
              console.error(`   startDate: ${period.startDate} → ${startDate}`);
              console.error(`   endDate: ${period.endDate} → ${endDate}`);
              return;
            }
            
            // 🔧 CORRECTION CRITIQUE: Traitement universel des compétences (nombres, chaînes, objets)
            console.log(`   🔍 Debug skills pour ${tech.technicianName}:`, tech.skills);
            console.log(`   🔍 Types des skills:`, (tech.skills || []).map(skill => `${typeof skill}:${skill}`));
            
            const cleanSkills = (tech.skills || []).map(skill => {
              // CAS 1: Skill est déjà un nombre (format serveur IA)
              if (typeof skill === 'number') {
                console.log(`   🔢 Skill numérique: ${skill} → ${skill}`);
                return skill;
              }
              
              // CAS 2: Skill est une chaîne de caractères
              if (typeof skill === 'string') {
                // Supprimer le préfixe "ID:" si présent et convertir en nombre
                const cleanSkill = skill.startsWith('ID:') ? skill.substring(3) : skill;
                const numberSkill = parseInt(cleanSkill);
                if (!isNaN(numberSkill)) {
                  console.log(`   🔤 Skill chaîne: "${skill}" → ${numberSkill}`);
                  return numberSkill;
                } else {
                  console.log(`   🔤 Skill chaîne non numérique: "${skill}" → "${cleanSkill}"`);
                  return cleanSkill;
                }
              }
              
              // CAS 3: Skill est un objet
              if (typeof skill === 'object' && skill !== null) {
                const skillValue = skill.id || skill.name || skill;
                console.log(`   📦 Skill objet: ${JSON.stringify(skill)} → ${skillValue}`);
                return skillValue;
              }
              
              // CAS 4: Autres types
              console.warn(`   ⚠️ Type skill inattendu: ${typeof skill}:${skill}`);
              return skill;
            });
            
            // Créer l'affectation
            const newAssignment = {
              technicianId: realTechnicianId,
              technician: matchingTech,
              startDate: startDate,
              endDate: endDate,
              skills: cleanSkills, // 🔧 Utiliser les skills nettoyés
              isWeekend: period.isWeekend || false,
              isHoliday: period.isHoliday || false,
              weekNumber: period.weekNumber || getWeek(startDate),
              estimatedPrime: (period.isHoliday || false) ? 600 : 478,
              technicianName: `${matchingTech.firstName} ${matchingTech.lastName}`,
              technicianSkills: matchingTech.skills || []
            };

            assignments.push(newAssignment);
            console.log(`   ➕ Affectation créée: ${matchingTech.firstName} ${matchingTech.lastName} (${format(startDate, 'dd/MM/yyyy', { locale: fr })}, prime: ${newAssignment.estimatedPrime}€)`);
            console.log(`   🎯 Compétences nettoyées: [${cleanSkills.join(', ')}] (originales: [${(tech.skills || []).join(', ')}])`);
          } else {
            console.error(`   ❌ Impossible de mapper le technicien:`, tech);
          }
        });
      } else {
        console.warn(`   ⚠️  Aucun technicien dans cette affectation:`, assignment);
      }
    });

    console.log(`\n📊 Conversion terminée: ${assignments.length} affectations créées`);
    
    // NOUVEAU: Post-traitement d'équilibrage automatique
    const balancedAssignments = autoBalanceAssignments(assignments);
    
    return balancedAssignments;
  };

  // NOUVELLE FONCTION: Équilibrage automatique post-IA
  const autoBalanceAssignments = (assignments) => {
    console.log('\n🎯 === POST-TRAITEMENT D\'ÉQUILIBRAGE AUTOMATIQUE ===');
    
    // Calculer la distribution actuelle
    const distribution = {};
    formData.selectedTechnicians.forEach(techId => {
      const tech = data.technicians.find(t => t.id === techId);
      distribution[techId] = {
        technician: tech,
        assignments: assignments.filter(a => a.technicianId === techId),
        count: assignments.filter(a => a.technicianId === techId).length
      };
    });
    
    const counts = Object.values(distribution).map(d => d.count);
    const average = counts.reduce((sum, count) => sum + count, 0) / counts.length;
    const minCount = Math.min(...counts);
    const maxCount = Math.max(...counts);
    
    console.log(`📊 Distribution avant équilibrage:`);
    Object.values(distribution).forEach(d => {
      const diff = d.count - average;
      const status = diff > 1 ? '📈 SUR-UTILISÉ' : diff < -1 ? '📉 SOUS-UTILISÉ' : '⚖️ ÉQUILIBRÉ';
      console.log(`   ${d.technician.firstName} ${d.technician.lastName}: ${d.count} (${diff >= 0 ? '+' : ''}${diff.toFixed(1)}) ${status}`);
    });
    
    const ecart = maxCount - minCount;
    console.log(`📊 Écart d'équité: ${ecart} astreinte(s) (min: ${minCount}, max: ${maxCount})`);
    
    // Si l'écart est trop important (>2), tenter un rééquilibrage
    if (maxCount - minCount > 2) {
      console.log('🔧 Écart détecté > 2, lancement du rééquilibrage INTELLIGENT...');
      
      // Identifier les sur-utilisés et sous-utilisés
      const overUtilized = Object.values(distribution).filter(d => d.count > average + 1);
      const underUtilized = Object.values(distribution).filter(d => d.count < average - 0.5);
      
      console.log(`🎯 Sur-utilisés: ${overUtilized.map(d => d.technician.firstName).join(', ')}`);
      console.log(`🎯 Sous-utilisés: ${underUtilized.map(d => d.technician.firstName).join(', ')}`);
      
      // Fonction pour vérifier la couverture d'une période
      const checkPeriodCoverage = (assignment, newTechnicianId) => {
        // Trouver toutes les affectations de cette période
        const periodAssignments = assignments.filter(a => 
          a.startDate.getTime() === assignment.startDate.getTime() &&
          a.endDate.getTime() === assignment.endDate.getTime()
        );
        
        // Remplacer temporairement le technicien
        const testAssignments = periodAssignments.map(a => 
          a === assignment ? { ...a, technicianId: newTechnicianId } : a
        );
        
        // Vérifier la couverture des compétences
        const coveredSkills = new Set();
        testAssignments.forEach(a => {
          const tech = data.technicians.find(t => t.id === a.technicianId);
          if (tech && tech.skills) {
            tech.skills.forEach(skillId => coveredSkills.add(skillId));
          }
        });
        
        const requiredSkills = formData.selectedSkills;
        const missingSkills = requiredSkills.filter(skillId => !coveredSkills.has(skillId));
        
        console.log(`    🔍 Test transfert vers ${data.technicians.find(t => t.id === newTechnicianId)?.firstName}:`);
        console.log(`       Compétences couvertes: ${coveredSkills.size}/${requiredSkills.length}`);
        console.log(`       Compétences manquantes: ${missingSkills.length}`);
        
        return missingSkills.length === 0; // Couverture complète requise
      };
      
      // Tenter des transferts d'affectations INTELLIGENTS
      let transfersCount = 0;
      const maxTransfers = 5; // Limite pour éviter les boucles infinies
      
      for (const overUser of overUtilized) {
        if (transfersCount >= maxTransfers) break;
        
        for (const underUser of underUtilized) {
          if (transfersCount >= maxTransfers) break;
          if (overUser.count - underUser.count <= 1) continue; // Pas d'amélioration significative
          
          // Chercher une affectation transférable qui MAINTIENT la couverture
          const transferableAssignment = overUser.assignments.find(assignment => {
            // 1. Vérifier que le sous-utilisé a les compétences de base
            const requiredSkills = assignment.skills || [];
            const underUserSkills = underUser.technician.skills || [];
            
            // Le technicien sous-utilisé doit couvrir au moins une compétence requise OU avoir des compétences complémentaires
            const hasRelevantSkills = requiredSkills.length === 0 || 
                                    requiredSkills.some(skillId => underUserSkills.includes(skillId)) ||
                                    underUserSkills.some(skillId => formData.selectedSkills.includes(skillId));
            
            if (!hasRelevantSkills) {
              console.log(`    ❌ ${underUser.technician.firstName} n'a pas les compétences requises`);
              return false;
            }
            
            // 2. NOUVEAU: Vérifier que le transfert maintient la couverture de période
            const maintainsCoverage = checkPeriodCoverage(assignment, underUser.technician.id);
            if (!maintainsCoverage) {
              console.log(`    ❌ Transfert vers ${underUser.technician.firstName} dégraderait la couverture`);
              return false;
            }
            
            // 3. Vérifier les contraintes d'absence (simplifiée)
            // TODO: Ajouter vérification complète des absences si nécessaire
            
            return true;
          });
          
          if (transferableAssignment) {
            console.log(`🔄 TRANSFERT INTELLIGENT: ${overUser.technician.firstName} → ${underUser.technician.firstName} (${format(transferableAssignment.startDate, 'dd/MM', { locale: fr })})`);
            
            // Effectuer le transfert
            transferableAssignment.technicianId = underUser.technician.id;
            transferableAssignment.technician = underUser.technician;
            transferableAssignment.technicianName = `${underUser.technician.firstName} ${underUser.technician.lastName}`;
            transferableAssignment.technicianSkills = underUser.technician.skills || [];
            
            // Mettre à jour les compteurs locaux
            overUser.count--;
            underUser.count++;
            
            // Recalculer les listes d'affectations
            overUser.assignments = assignments.filter(a => a.technicianId === overUser.technician.id);
            underUser.assignments = assignments.filter(a => a.technicianId === underUser.technician.id);
            
            transfersCount++;
            
            // Vérifier si l'équilibrage est suffisant
            const newGap = Math.max(...Object.values(distribution).map(d => d.count)) - 
                          Math.min(...Object.values(distribution).map(d => d.count));
            if (newGap <= 2) {
              console.log('✅ Équilibrage satisfaisant atteint');
              break;
            }
          }
        }
      }
      
      console.log(`🔧 ${transfersCount} transfert(s) intelligent(s) effectué(s)`);
    } else {
      console.log('✅ Distribution déjà équilibrée, aucun transfert nécessaire');
    }
    
    // Afficher la distribution finale
    const finalDistribution = {};
    formData.selectedTechnicians.forEach(techId => {
      const tech = data.technicians.find(t => t.id === techId);
      finalDistribution[techId] = {
        technician: tech,
        count: assignments.filter(a => a.technicianId === techId).length
      };
    });
    
    const finalCounts = Object.values(finalDistribution).map(d => d.count);
    const finalAverage = finalCounts.reduce((sum, count) => sum + count, 0) / finalCounts.length;
    const finalMinCount = Math.min(...finalCounts);
    const finalMaxCount = Math.max(...finalCounts);
    
    console.log(`\n📊 Distribution après équilibrage intelligent:`);
    Object.values(finalDistribution).forEach(d => {
      const diff = d.count - finalAverage;
      const status = diff > 1 ? '📈' : diff < -1 ? '📉' : '⚖️';
      console.log(`   ${d.technician.firstName} ${d.technician.lastName}: ${d.count} (${diff >= 0 ? '+' : ''}${diff.toFixed(1)}) ${status}`);
    });
    
    console.log(`📊 Écart final min-max: ${finalMinCount}-${finalMaxCount}`);
    console.log('✅ Post-traitement d\'équilibrage terminé\n');
    
    return assignments;
  };

  // NOUVEAU: Fonction pour analyser et logger une solution (appelée seulement pour les meilleures)
  const analyzeAndLogSolution = (assignments, initialTechnicianStats, iteration, score, isFinale = false, sessionId, context) => {
    console.log('\n📊 === RÉCAPITULATIF AFFECTATIONS PAR TECHNICIEN ===');
    
    // Calculer les nouvelles affectations par technicien
    const newAssignmentsByTech = {};
    assignments.forEach(assignment => {
      const techId = assignment.technicianId;
      if (!newAssignmentsByTech[techId]) {
        newAssignmentsByTech[techId] = 0;
      }
      newAssignmentsByTech[techId]++;
    });
    
    // Afficher le récapitulatif pour tous les techniciens sélectionnés
    const technicianStats = [];
    const planningYear = formData.startDate.getFullYear();
    formData.selectedTechnicians.forEach(techId => {
      const tech = data.technicians.find(t => t.id === techId);
      if (tech) {
        // Calculer les affectations existantes pour l'année de planification
        const existingAssignments = data.existingOnCalls.filter(onCall => {
          const year = new Date(onCall.startDate).getFullYear();
          return year === planningYear && onCall.technicianId === techId;
        }).length;
        
        const newAssignments = newAssignmentsByTech[techId] || 0;
        const totalAssignments = existingAssignments + newAssignments;
        
        // NOUVEAU: Récupérer les informations d'équilibrage de période
        const techStats = initialTechnicianStats[techId] || {};
        const periodTarget = techStats.periodTarget || 5; // Objectif ajusté pour la période
        const finalDeficit = Math.max(0, periodTarget - totalAssignments);
        const finalExcess = Math.max(0, totalAssignments - periodTarget);
        
        technicianStats.push({
          tech,
          existingAssignments,
          newAssignments,
          totalAssignments,
          periodTarget, // Utiliser l'objectif de période
          finalDeficit,
          finalExcess,
          initialPriorityScore: techStats.priorityScore || 0
        });
      }
    });
    
    // Trier par ordre alphabétique (nom de famille)
    technicianStats.sort((a, b) => a.tech.lastName.localeCompare(b.tech.lastName, 'fr'));
    
    // Calculer les statistiques globales
    const totalExisting = technicianStats.reduce((sum, stat) => sum + stat.existingAssignments, 0);
    const totalNew = technicianStats.reduce((sum, stat) => sum + stat.newAssignments, 0);
    const totalAll = technicianStats.reduce((sum, stat) => sum + stat.totalAssignments, 0);
    const averageTotal = totalAll / technicianStats.length;
    
    console.log(`📈 STATISTIQUES GLOBALES (Itération ${iteration}):`);
    console.log(`   • Techniciens analysés: ${technicianStats.length}`);
    console.log(`   • Affectations existantes (${planningYear}): ${totalExisting}`);
    console.log(`   • Nouvelles affectations planifiées: ${totalNew}`);
    console.log(`   • Total après planification: ${totalAll}`);
    console.log(`   • Moyenne par technicien: ${averageTotal.toFixed(1)}`);
    console.log(`   • Score de la solution: ${score.toFixed(2)}`);
    
    console.log(`\n👥 DÉTAIL PAR TECHNICIEN (trié alphabétiquement):`);
    technicianStats.forEach((stat, index) => {
      const diffFromAverage = stat.totalAssignments - averageTotal;
      const diffSign = diffFromAverage >= 0 ? '+' : '';
      const balanceIcon = Math.abs(diffFromAverage) <= 1 ? '⚖️' : 
                         diffFromAverage > 1 ? '📈' : '📉';
      
      // NOUVEAU: Indicateurs d'équilibrage de période
      const periodStatus = stat.finalDeficit > 0 ? `📉 Déficit ${stat.finalDeficit}` :
                          stat.finalExcess > 0 ? `📈 Excès ${stat.finalExcess}` : '✅ Objectif atteint';
      const priorityIcon = stat.initialPriorityScore >= 1000 ? '🔥' :
                          stat.initialPriorityScore >= 600 ? '⚡' :
                          stat.initialPriorityScore >= 400 ? '⚖️' : '❄️';
      
      console.log(`   ${index + 1}. ${balanceIcon} ${stat.tech.firstName} ${stat.tech.lastName}:`);
      console.log(`      • Existantes (${planningYear}): ${stat.existingAssignments}`);
      console.log(`      • Nouvelles: ${stat.newAssignments}`);
      console.log(`      • TOTAL: ${stat.totalAssignments}/${stat.periodTarget} (${diffSign}${diffFromAverage.toFixed(1)} vs moyenne)`);
      console.log(`      • ÉQUILIBRAGE PÉRIODE: ${periodStatus} ${priorityIcon}`);
    });
    
    // Calculer l'écart-type pour mesurer l'équilibrage
    const variance = technicianStats.reduce((sum, stat) => {
      return sum + Math.pow(stat.totalAssignments - averageTotal, 2);
    }, 0) / technicianStats.length;
    const standardDeviation = Math.sqrt(variance);
    
    console.log(`\n📊 ANALYSE D'ÉQUILIBRAGE:`);
    console.log(`   • Écart-type des charges: ${standardDeviation.toFixed(2)}`);
    console.log(`   • Technicien le plus chargé: ${technicianStats[0].tech.firstName} ${technicianStats[0].tech.lastName} (${technicianStats[0].totalAssignments})`);
    console.log(`   • Technicien le moins chargé: ${technicianStats[technicianStats.length - 1].tech.firstName} ${technicianStats[technicianStats.length - 1].tech.lastName} (${technicianStats[technicianStats.length - 1].totalAssignments})`);
    console.log(`   • Écart max: ${technicianStats[0].totalAssignments - technicianStats[technicianStats.length - 1].totalAssignments} astreinte(s)`);
    
    // Évaluation de la qualité de l'équilibrage (adaptée selon la durée)
    const maxGap = technicianStats[0].totalAssignments - technicianStats[technicianStats.length - 1].totalAssignments;
    
    // Calculer le nombre de périodes dans la planification
    const periodsInPlanning = assignments ? assignments.length : 0;
    let balanceQuality = 'Excellent';
    
    if (periodsInPlanning >= 20) { // Critères ultra-stricts pour semestres
      if (maxGap > 2) {
        balanceQuality = 'Inacceptable';
      } else if (maxGap > 1) {
        balanceQuality = 'Médiocre';
      } else if (standardDeviation > 0.6) {
        balanceQuality = 'Bon';
      } else {
        balanceQuality = 'Excellent';
      }
    } else if (periodsInPlanning >= 10) { // Critères stricts pour trimestres
      if (maxGap > 3) {
        balanceQuality = 'Médiocre';
      } else if (maxGap > 2) {
        balanceQuality = 'Moyen';
      } else if (standardDeviation > 1) {
        balanceQuality = 'Bon';
      } else {
        balanceQuality = 'Excellent';
      }
    } else { // Critères normaux pour courtes périodes
      if (standardDeviation > 2) {
        balanceQuality = 'Médiocre';
      } else if (standardDeviation > 1.5) {
        balanceQuality = 'Moyen';
      } else if (standardDeviation > 1) {
        balanceQuality = 'Bon';
      }
    }
    
    console.log(`   • Qualité de l'équilibrage: ${balanceQuality} (écart-type: ${standardDeviation.toFixed(2)})`);
    
    // Identifier les techniciens sous-utilisés et sur-utilisés
    const underutilized = technicianStats.filter(stat => stat.totalAssignments < averageTotal - 1);
    const overutilized = technicianStats.filter(stat => stat.totalAssignments > averageTotal + 1);
    
    if (underutilized.length > 0) {
      console.log(`\n📉 TECHNICIENS SOUS-UTILISÉS (${underutilized.length}):`);
      underutilized.forEach(stat => {
        console.log(`   • ${stat.tech.firstName} ${stat.tech.lastName}: ${stat.totalAssignments} (${(stat.totalAssignments - averageTotal).toFixed(1)} vs moyenne)`);
      });
    }
    
    if (overutilized.length > 0) {
      console.log(`\n📈 TECHNICIENS SUR-UTILISÉS (${overutilized.length}):`);
      overutilized.forEach(stat => {
        console.log(`   • ${stat.tech.firstName} ${stat.tech.lastName}: ${stat.totalAssignments} (+${(stat.totalAssignments - averageTotal).toFixed(1)} vs moyenne)`);
      });
    }
    
    // NOUVEAU: Écrire dans un fichier de log sur le serveur (SEULEMENT pour le log final)
    if (isFinale) { // Seulement pour le log final de la planification
      writeToServerLogFile({
        // Informations de session
        sessionId: sessionId,
        sessionContext: context,
        
        // Informations de planification
        timestamp: new Date().toISOString(),
        type: 'PLANNING_ANALYSIS',
        iteration: iteration,
        score: score,
        planningPeriod: formData.startDate && formData.endDate ? {
          start: format(formData.startDate, 'dd/MM/yyyy', { locale: fr }),
          end: format(formData.endDate, 'dd/MM/yyyy', { locale: fr })
        } : null,
        selectedSkills: formData.selectedSkills.map(skillId => {
          const skill = data.skills.find(s => s.id === skillId);
          return skill ? skill.name : skillId;
        }),
        maxTechniciansPerPeriod: formData.maxTechniciansPerPeriod,
        
        // Statistiques globales
        globalStats: {
          techniciansCount: technicianStats.length,
          existingAssignments: totalExisting,
          newAssignments: totalNew,
          totalAssignments: totalAll,
          averagePerTechnician: averageTotal,
          standardDeviation: standardDeviation,
          balanceQuality: balanceQuality
        },
        
        // Détails par technicien
        technicianDetails: technicianStats.map(stat => ({
          name: `${stat.tech.firstName} ${stat.tech.lastName}`,
          existing: stat.existingAssignments,
          new: stat.newAssignments,
          total: stat.totalAssignments,
          diffFromAverage: stat.totalAssignments - averageTotal,
          // NOUVEAU: Informations d'équilibrage annuel
          periodTarget: stat.periodTarget,
          periodDeficit: stat.finalDeficit,
          periodExcess: stat.finalExcess,
          priorityScore: stat.initialPriorityScore
        })),
        
        // Analyse des déséquilibres
        underutilized: underutilized.map(stat => ({
          name: `${stat.tech.firstName} ${stat.tech.lastName}`,
          total: stat.totalAssignments,
          deficit: averageTotal - stat.totalAssignments
        })),
        overutilized: overutilized.map(stat => ({
          name: `${stat.tech.firstName} ${stat.tech.lastName}`,
          total: stat.totalAssignments,
          excess: stat.totalAssignments - averageTotal
        }))
      });
    }
    
    console.log('\n=== FIN RÉCAPITULATIF AFFECTATIONS ===\n');
  };

  // NOUVEAU: Fonction pour écrire dans un fichier de log sur le serveur
  const writeToServerLogFile = async (logData) => {
    try {
      // Envoyer les données au serveur via l'API
      const response = await fetch('http://localhost:3001/api/save-planning-log', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`📄 Log de planification sauvegardé sur le serveur: ${result.filename}`);
        console.log(`📁 Chemin: astreintes-server/logs/${result.filename}`);
      } else {
        const error = await response.json();
        console.error('Erreur lors de la sauvegarde sur le serveur:', error);
        
        // Fallback vers localStorage en cas d'erreur serveur
        const jsonContent = JSON.stringify(logData, null, 2);
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('planning-analysis-latest', jsonContent);
          console.log('📄 Fallback: Analyse sauvegardée dans localStorage');
        }
      }
      
    } catch (error) {
      console.error('Erreur de connexion au serveur pour le log:', error);
      
      // Fallback vers localStorage en cas d'erreur de connexion
      try {
        const jsonContent = JSON.stringify(logData, null, 2);
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('planning-analysis-latest', jsonContent);
          console.log('📄 Fallback: Analyse sauvegardée dans localStorage (serveur inaccessible)');
        }
      } catch (localError) {
        console.error('Erreur lors de la sauvegarde locale:', localError);
      }
    }
  };

  // Fonction utilitaire pour formater la durée
  const formatDuration = (ms) => {
    if (ms < 1000) {
      return `${ms}ms`;
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(ms / 60000);
      const seconds = ((ms % 60000) / 1000).toFixed(1);
      return `${minutes}m ${seconds}s`;
    }
  };

  const analyzeCoverageNeeds = (totalPeriods) => {
    const needs = {};
    
    formData.selectedSkills.forEach(skillId => {
      const skill = data.skills.find(s => s.id === skillId);
      if (skill && skill.coverageIndex) {
        const requiredPeriods = Math.ceil((skill.coverageIndex / 100) * totalPeriods);
        needs[skillId] = {
          skill,
          requiredPeriods,
          coveredPeriods: 0,
          priority: skill.coverageIndex // Plus l'indice est élevé, plus c'est prioritaire
        };
      }
    });
    
    return needs;
  };

  const generateIntelligentSolution = async (periods, initialTechnicianStats, coverageNeeds, iteration) => {
    const assignments = [];
    const technicianStats = JSON.parse(JSON.stringify(initialTechnicianStats));
    const remainingCoverageNeeds = JSON.parse(JSON.stringify(coverageNeeds));
    
    // Stratégie adaptative selon l'itération
    const isEarlyIteration = iteration <= 60; // Augmenté de 30 à 60 pour plus de focus sur la couverture
    const isMiddleIteration = iteration > 60 && iteration <= 140; // Ajusté pour 200 itérations
    const isLateIteration = iteration > 140; // Phase de réparation plus longue
    
    console.log(`Stratégie: ${isEarlyIteration ? 'Couverture prioritaire' : isMiddleIteration ? 'Équilibrage mixte' : 'Réparation ciblée'}`);
    
    // Trier les périodes selon la stratégie
    const orderedPeriods = [...periods];
    if (isEarlyIteration || isLateIteration) {
      // Ordre chronologique pour couverture systématique ou réparation
      orderedPeriods.sort((a, b) => a.startDate - b.startDate);
    } else {
      // Randomiser modérément en phase intermédiaire
      for (let i = orderedPeriods.length - 1; i > 0; i--) {
        if (Math.random() < 0.4) { // Augmenté de 0.3 à 0.4 pour plus de diversité
          const j = Math.floor(Math.random() * (i + 1));
          [orderedPeriods[i], orderedPeriods[j]] = [orderedPeriods[j], orderedPeriods[i]];
        }
      }
    }
    
    // Phase 1 : Affectation principale - PLUSIEURS TECHNICIENS PAR PÉRIODE
    for (const period of orderedPeriods) {
      try {
        console.log(`\n📅 Affectation pour la période ${format(period.startDate, 'dd/MM/yyyy', { locale: fr })}`);
        
        // Affecter plusieurs techniciens à cette période (minimum 3, maximum selon paramètre)
        const minTechniciansPerPeriod = 3;
        const maxTechniciansPerPeriod = formData.maxTechniciansPerPeriod;
        let assignedToThisPeriod = 0;
        
        // Continuer à affecter des techniciens jusqu'à atteindre le maximum ou ne plus avoir de candidats valides
        while (assignedToThisPeriod < maxTechniciansPerPeriod) {
          // Analyser les compétences déjà couvertes sur cette période
          const alreadyCoveredSkills = assignments
            .filter(a => a.startDate.getTime() === period.startDate.getTime())
            .flatMap(a => a.skills);
          
          // Identifier les compétences critiques non encore couvertes
          const criticalMissingSkills = formData.selectedSkills.filter(skillId => {
            const skill = data.skills.find(s => s.id === skillId);
            const isCritical = skill && skill.coverageIndex >= 80; // Compétences critiques
            const notYetCovered = !alreadyCoveredSkills.includes(skillId);
            return isCritical && notYetCovered;
          });
          
          console.log(`🔍 Période ${format(period.startDate, 'dd/MM/yyyy', { locale: fr })}: Compétences critiques manquantes: [${criticalMissingSkills.map(id => data.skills.find(s => s.id === id)?.name).join(', ')}]`);
          
          const assignment = await findBestTechnicianForPeriodIntelligent(
            period, 
            technicianStats, 
            assignments,
            remainingCoverageNeeds,
            {
              prioritizeCoverage: isEarlyIteration,
              balanceLoad: isMiddleIteration,
              allowRandomness: false,
              targetCriticalSkills: isLateIteration,
              iteration,
              periodAssignments: assignedToThisPeriod,
              allowMultiplePerPeriod: true,
              alreadyCoveredSkills: alreadyCoveredSkills,
              periodStartTime: period.startDate,
              criticalMissingSkills: criticalMissingSkills
            }
          );
          
          if (assignment) {
            assignments.push(assignment);
            assignedToThisPeriod++;
            
            // Mise à jour simplifiée des stats temporaires pour l'algorithme uniquement
            // (le vrai calcul se fera dans calculatePlanningStats)
            if (technicianStats[assignment.technicianId]) {
              technicianStats[assignment.technicianId].tempAssignments += 1;
              
              const assignmentEndDate = new Date(assignment.endDate);
              if (!technicianStats[assignment.technicianId].lastAssignmentDate || 
                  assignmentEndDate > technicianStats[assignment.technicianId].lastAssignmentDate) {
                technicianStats[assignment.technicianId].lastAssignmentDate = assignmentEndDate;
                console.log(`📝 Mise à jour lastAssignmentDate pour ${assignment.technician.firstName} ${assignment.technician.lastName}: ${format(assignmentEndDate, 'dd/MM/yyyy', { locale: fr })}`);
              }
            }
            
            // Mettre à jour les besoins de couverture
            assignment.skills.forEach(skillId => {
              if (remainingCoverageNeeds[skillId]) {
                remainingCoverageNeeds[skillId].coveredPeriods++;
              }
            });
            
            console.log(`✅ Technicien ${assignedToThisPeriod}/${maxTechniciansPerPeriod} affecté: ${assignment.technician.firstName} ${assignment.technician.lastName}`);
          } else {
            // Aucun technicien disponible supplémentaire pour cette période
            console.log(`⚠️ Aucun technicien supplémentaire disponible (${assignedToThisPeriod} affecté(s))`);
            break;
          }
        }
        
        // Vérifier si on a le minimum requis
        if (assignedToThisPeriod < minTechniciansPerPeriod) {
          console.log(`⚠️ Période sous-staffée: seulement ${assignedToThisPeriod} technicien(s) au lieu de ${minTechniciansPerPeriod} minimum`);
        } else {
          console.log(`✅ Période correctement staffée: ${assignedToThisPeriod} technicien(s)`);
        }
        
      } catch (error) {
        console.log(`⚠️ Période non affectée: ${format(period.startDate, 'dd/MM/yyyy', { locale: fr })} - ${error.message}`);
      }
    }
    
    // Phase 2 : Réparation ciblée des compétences critiques (en fin d'itération)
    if (isLateIteration && assignments.length > 0) {
      await repairCriticalSkillsCoverage(assignments, technicianStats, remainingCoverageNeeds, periods);
    }
    
    return { assignments };
  };

  const repairCriticalSkillsCoverage = async (assignments, technicianStats, coverageNeeds, allPeriods) => {
    console.log('\n🔧 === PHASE DE RÉPARATION CIBLÉE ===');
    
    // Identifier les compétences les plus critiques (déficit important)
    const criticalSkills = Object.entries(coverageNeeds)
      .filter(([skillId, need]) => {
        const deficit = need.requiredPeriods - need.coveredPeriods;
        return deficit > 0;
      })
      .sort((a, b) => {
        const deficitA = a[1].requiredPeriods - a[1].coveredPeriods;
        const deficitB = b[1].requiredPeriods - b[1].coveredPeriods;
        // Trier par déficit relatif (déficit / requis)
        const relativeDeficitA = deficitA / a[1].requiredPeriods;
        const relativeDeficitB = deficitB / b[1].requiredPeriods;
        return relativeDeficitB - relativeDeficitA;
      });
    
    if (criticalSkills.length === 0) {
      console.log('✅ Aucune compétence critique à réparer');
      return;
    }
    
    console.log('Compétences critiques à réparer:');
    criticalSkills.forEach(([skillId, need]) => {
      const skill = data.skills.find(s => s.id === skillId);
      const deficit = need.requiredPeriods - need.coveredPeriods;
      console.log(`- ${skill?.name}: ${deficit} période(s) manquante(s)`);
    });
    
    // Tentative de réassignation ciblée
    for (const [criticalSkillId, criticalNeed] of criticalSkills.slice(0, 3)) { // Traiter les 3 plus critiques
      const deficit = criticalNeed.requiredPeriods - criticalNeed.coveredPeriods;
      if (deficit <= 0) continue;
      
      const skill = data.skills.find(s => s.id === criticalSkillId);
      console.log(`\n🎯 Réparation de ${skill?.name} (déficit: ${deficit})`);
      
      // Trouver les techniciens pouvant couvrir cette compétence
      const capableTechnicians = formData.selectedTechnicians.filter(techId => {
        const tech = data.technicians.find(t => t.id === techId);
        return tech && tech.skills && tech.skills.includes(criticalSkillId);
      });
      
      if (capableTechnicians.length === 0) {
        console.log(`❌ Aucun technicien disponible pour ${skill?.name}`);
        continue;
      }
      
      // Trouver les périodes non encore affectées ou réassignables
      const availablePeriods = allPeriods.filter(period => {
        return !assignments.some(assignment => 
          assignment.startDate.getTime() === period.startDate.getTime()
        );
      });
      
      if (availablePeriods.length === 0) {
        // Essayer la réassignation de périodes existantes si possible
        console.log(`⚠️ Toutes les périodes sont affectées, réassignation non implémentée pour ${skill?.name}`);
        continue;
      }
      
      // Affecter des périodes supplémentaires pour cette compétence
      let repairedCount = 0;
      for (const period of availablePeriods.slice(0, deficit)) {
        try {
          // Trouver le meilleur technicien pour cette période avec focus sur la compétence critique
          const bestTech = await findBestTechnicianForCriticalSkill(
            period, 
            criticalSkillId, 
            capableTechnicians, 
            technicianStats, 
            assignments
          );
          
          if (bestTech) {
            assignments.push(bestTech);
            
            // Mettre à jour les stats temporaires (simplifié)
            if (technicianStats[bestTech.technicianId]) {
              technicianStats[bestTech.technicianId].tempAssignments += 1;
              
              const assignmentEndDate = new Date(bestTech.endDate);
              if (!technicianStats[bestTech.technicianId].lastAssignmentDate || 
                  assignmentEndDate > technicianStats[bestTech.technicianId].lastAssignmentDate) {
                technicianStats[bestTech.technicianId].lastAssignmentDate = assignmentEndDate;
              }
            }
            
            // Mettre à jour la couverture
            bestTech.skills.forEach(skillId => {
              if (coverageNeeds[skillId]) {
                coverageNeeds[skillId].coveredPeriods++;
              }
            });
            
            repairedCount++;
            console.log(`✅ Période réparée: ${format(period.startDate, 'dd/MM/yyyy', { locale: fr })} → ${bestTech.technician.firstName} ${bestTech.technician.lastName}`);
          }
        } catch (error) {
          console.log(`❌ Impossible de réparer la période ${format(period.startDate, 'dd/MM/yyyy', { locale: fr })}: ${error.message}`);
        }
      }
      
      console.log(`🔧 ${skill?.name}: ${repairedCount} période(s) réparée(s)`);
    }
  };

  const findBestTechnicianForCriticalSkill = async (period, criticalSkillId, capableTechnicians, technicianStats, currentAssignments) => {
    const candidates = [];
    
    for (const techId of capableTechnicians) {
      const tech = data.technicians.find(t => t.id === techId);
      const stats = technicianStats[techId];
      
      // Vérifications de base (contraintes hard)
      if (!isValidCandidate(tech, stats, period, currentAssignments, false)) {
        continue;
      }
      
      // Calculer les compétences que ce technicien peut couvrir
      const skillsToCover = stats.skills.filter(skillId => 
        formData.selectedSkills.includes(skillId)
      );
      
      if (!skillsToCover.includes(criticalSkillId)) {
        continue; // Le technicien doit avoir la compétence critique
      }
      
      // Score simple : priorité au technicien le moins chargé ayant la compétence
      const currentLoad = stats.currentAssignments + stats.tempAssignments;
      const score = 100 - currentLoad; // Plus la charge est faible, meilleur est le score
      
      candidates.push({
        technicianId: techId,
        technician: tech,
        skillsToCover,
        score,
        stats
      });
    }
    
    if (candidates.length === 0) {
      throw new Error('Aucun candidat valide pour la compétence critique (contrainte de repos respectée)');
    }
    
    // Sélectionner le candidat avec le meilleur score
    candidates.sort((a, b) => b.score - a.score);
    const selected = candidates[0];
    
    return {
      technicianId: selected.technicianId,
      technician: selected.technician,
      startDate: period.startDate,
      endDate: period.endDate,
      skills: selected.skillsToCover,
      isWeekend: period.isWeekend,
      isHoliday: period.isHoliday,
      weekNumber: period.weekNumber,
      estimatedPrime: period.isHoliday ? 600 : 478,
      hasConstraintViolations: false // Plus jamais de violations
    };
  };

  const findBestTechnicianForPeriodIntelligent = async (period, technicianStats, currentAssignments, coverageNeeds, strategy) => {
    const candidates = [];
    
    console.log(`\n=== AFFECTATION SIMPLIFIÉE ÉQUILIBRÉE ===`);
    console.log('Période:', format(period.startDate, 'dd/MM/yyyy', { locale: fr }));
    console.log('Stratégie:', strategy);
    
    // Compétences rares prioritaires (inspiré Python)
    const rareSkills = ['LANSECU', 'COE', 'CISCO'];
    const commonSkills = ['AVAYA', 'MITEL', 'OXE'];
    
    // Analyser tous les techniciens candidats de manière équilibrée
    for (const techId of formData.selectedTechnicians) {
      const tech = data.technicians.find(t => t.id === techId);
      const stats = technicianStats[techId];
      
      // Vérifications de base (contraintes hard)
      if (!isValidCandidate(tech, stats, period, currentAssignments, true)) { // allowFlexibility = true
        continue;
      }
      
      // Calculer les compétences que ce technicien peut couvrir
      const skillsToCover = stats.skills.filter(skillId => {
        if (!formData.selectedSkills.includes(skillId)) {
          return false;
        }
        
        // Vérifier si la compétence est critique
        const skill = data.skills.find(s => s.id === skillId);
        if (skill && skill.critical) {
          // Pour les compétences critiques, vérifier le niveau du technicien
          const techSkillInfo = tech.skills?.find(s => 
            (typeof s === 'object' ? s.skillId : s) === skillId
          );
          
          if (techSkillInfo && typeof techSkillInfo === 'object') {
            // Le technicien doit avoir un niveau 2 pour les compétences critiques
            if (techSkillInfo.level < 2) {
              console.log(`${tech.firstName} ${tech.lastName}: Compétence critique "${skill.name}" nécessite niveau 2 (a niveau ${techSkillInfo.level})`);
              return false;
            }
          } else {
            // Si pas d'info de niveau, considérer comme niveau 1 par défaut
            console.log(`${tech.firstName} ${tech.lastName}: Compétence critique "${skill.name}" nécessite niveau 2 (niveau inconnu)`);
            return false;
          }
        }
        
        return true;
      });
      
      if (skillsToCover.length === 0) {
        console.log(`${tech.firstName} ${tech.lastName}: Aucune compétence pertinente ou niveau insuffisant`);
        continue;
      }
      
      // SCORE ÉQUILIBRÉ (inspiré Python mais moins extrême)
      const currentLoad = stats.currentAssignments + stats.tempAssignments;
      const targetLoad = stats.periodTarget || 5;
      
      let score = 100; // Score de base
      
      // 1. ÉQUILIBRAGE MODÉRÉ 
      if (currentLoad < targetLoad) {
        const deficit = targetLoad - currentLoad;
        score += deficit * 200; // Bonus modéré pour déficit (réduit de 400 à 200)
        console.log(`    📈 DÉFICIT: ${deficit} → Bonus +${deficit * 200}`);
      } else if (currentLoad > targetLoad) {
        const excess = currentLoad - targetLoad;
        score -= excess * 150; // Pénalité modérée pour excès (réduit de 600 à 150)
        console.log(`    📉 EXCÈS: ${excess} → Pénalité -${excess * 150}`);
      } else {
        score += 50; // Bonus léger pour équilibre parfait
        console.log(`    ⚖️ ÉQUILIBRÉ → Bonus +50`);
      }
      
      // 2. COMPÉTENCES RARES (bonus réduit)
      let rareSkillBonus = 0;
      const rareSkillsCovered = skillsToCover.filter(skill => rareSkills.includes(skill));
      const commonSkillsCovered = skillsToCover.filter(skill => commonSkills.includes(skill));
      
      rareSkillBonus += rareSkillsCovered.length * 80; // Bonus réduit pour compétences rares (150→80)
      rareSkillBonus += commonSkillsCovered.length * 40;  // Bonus réduit pour communes (50→40)
      
      score += rareSkillBonus;
      console.log(`    🛠️ COMPÉTENCES: ${rareSkillsCovered.length} rares (+${rareSkillsCovered.length * 80}), ${commonSkillsCovered.length} communes (+${commonSkillsCovered.length * 40})`);
      
      // 3. REPOS (simplifié)
      if (!stats.lastAssignmentDate) {
        score += 50; // Bonus réduit pour repos (80→50)
        console.log(`    💤 REPOS: Aucune affectation récente → Bonus +50`);
      }
      
      console.log(`${tech.firstName} ${tech.lastName}: Score FINAL ${score.toFixed(2)} ` +
        `(charge: ${currentLoad}/${targetLoad}) ` +
        `[${skillsToCover.map(id => data.skills.find(s => s.id === id)?.name).join(', ')}]`);
      
      candidates.push({
        technicianId: techId,
        technician: tech,
        skillsToCover,
        score: { totalScore: score },
        stats
      });
    }
    
    if (candidates.length === 0) {
      throw new Error('Aucun candidat valide trouvé');
    }
    
    // Sélectionner le meilleur candidat
    candidates.sort((a, b) => b.score.totalScore - a.score.totalScore);
    const selected = candidates[0];
    
    console.log(`🎯 Sélectionné: ${selected.technician.firstName} ${selected.technician.lastName} (score: ${selected.score.totalScore.toFixed(2)})`);
    
    return {
      technicianId: selected.technicianId,
      technician: selected.technician,
      startDate: period.startDate,
      endDate: period.endDate,
      skills: selected.skillsToCover,
      isWeekend: period.isWeekend,
      isHoliday: period.isHoliday,
      weekNumber: period.weekNumber,
      estimatedPrime: period.isHoliday ? 600 : 478,
      hasConstraintViolations: selected.score.hasConstraintViolations
    };
  };

  // Fonction utilitaire pour calculer le nombre de périodes d'astreinte entre deux dates
  const calculatePeriodsBetweenDates = (endDate, startDate) => {
    // Une période d'astreinte = 7 jours (vendredi à jeudi)
    // Le repos requis = 2 périodes complètes = 14 jours entre les fins de période
    const daysDifference = Math.floor((startDate - endDate) / (1000 * 60 * 60 * 24));
    
    // Calculer le nombre de périodes complètes écoulées
    // 1 période = 7 jours, donc diviser par 7 et arrondir vers le bas
    const completePeriods = Math.floor(daysDifference / 7);
    
    return {
      daysDifference,
      completePeriods,
      isValidRest: completePeriods >= 2 // Au moins 2 périodes de repos
    };
  };

  const isValidCandidate = (tech, stats, period, currentAssignments, allowFlexibility) => {
    const techId = tech.id;
    
    // Vérifier si le technicien est sélectionné pour cette planification
    if (!formData.selectedTechnicians.includes(techId)) {
      return false;
    }
    
    // Vérifier les absences (contrainte dure, sauf si bypass activé au niveau de l'absence)
    if (stats.absences && stats.absences.length > 0) {
      const hasBlockingAbsence = stats.absences.some(absence => {
        const absenceStart = new Date(absence.startDate);
        const absenceEnd = new Date(absence.endDate);
        const periodStart = new Date(period.startDate);
        const periodEnd = new Date(period.endDate);
        
        // Chevauchement d'absence
        const hasOverlap = (absenceStart <= periodEnd && absenceEnd >= periodStart);
        
        if (!hasOverlap) return false;
        
        // Si l'absence a un bypass activé, elle n'est pas bloquante
        if (absence.bypassAbsenceValidation === true) {
          console.log(`    ✅ BYPASS ABSENCE: ${tech.firstName} ${tech.lastName} - absence ${absence.type} avec bypass activé`);
          return false;
        }
        
        // Sinon, vérifier le type d'absence pour déterminer si elle est bloquante
        const absenceType = absence.type?.toUpperCase();
        const blockingTypes = ['CA', 'CP', 'MALADIE', 'FORMATION', 'CM', 'TPS', 'RECUP', 'MEDICAL'];
        
        // Les absences RECUP_ASTREINTE ne sont pas bloquantes (informatives seulement)
        if (absenceType === 'RECUP_ASTREINTE') {
          console.log(`    ℹ️  RECUP_ASTREINTE: ${tech.firstName} ${tech.lastName} - absence informative, non bloquante`);
          return false;
        }
        
        return blockingTypes.includes(absenceType);
      });
      
      if (hasBlockingAbsence) {
        console.log(`    ❌ ABSENCE BLOQUANTE: ${tech.firstName} ${tech.lastName} en absence critique sans bypass`);
        return false;
      }
    }
    
    // Vérifier le repos minimum entre astreintes (contrainte dure)
    if (stats.lastAssignmentDate) {
      const lastAssignmentEnd = new Date(stats.lastAssignmentDate);
      const currentPeriodStart = new Date(period.startDate);
      const daysBetween = Math.floor((currentPeriodStart - lastAssignmentEnd) / (1000 * 60 * 60 * 24));
      
      if (daysBetween < 14) { // 2 semaines minimum comme dans le script Python
        return false;
      }
    }
    
    // Validation d'équilibrage SOUPLE (pas trop strict)
    const currentLoad = stats.currentAssignments + stats.tempAssignments;
    const futureLoad = currentLoad + 1;
    const targetLoad = stats.periodTarget || 5;
    
    // Limite plus souple : permettre jusqu'à +3 au lieu de +2
    const maxAllowedLoad = targetLoad + 3;
    
    if (!allowFlexibility && futureLoad > maxAllowedLoad) {
      console.log(`    ⚠️ LIMITE SOUPLE: ${tech.firstName} ${tech.lastName} dépasserait limite (${futureLoad} > ${maxAllowedLoad})`);
      return false;
    }
    
    // Vérifier les compétences requises
    const hasRequiredSkills = stats.skills && stats.skills.some(skillId => 
      formData.selectedSkills.includes(skillId)
    );
    
    if (!hasRequiredSkills) {
      return false;
    }
    
    return true;
  };

  const calculateCandidateScore = (techId, skillsToCover, techStats, allTechStats, coverageNeeds, strategy) => {
    let score = 0;
    
    // DEBUG SPÉCIAL pour Steven HOUY
    const isHouy = techStats.technician?.firstName === 'Steven' && techStats.technician?.lastName === 'HOUY';
    if (isHouy) {
      console.log(`\n🔍 DEBUG STEVEN HOUY - Calcul du score`);
      console.log(`   📋 Compétences HOUY: [${skillsToCover.map(id => data.skills.find(s => s.id === id)?.name).join(', ')}]`);
      console.log(`   📊 Charge actuelle: ${techStats.currentAssignments}, temp: ${techStats.tempAssignments}`);
    }
    
    // 1. Score de couverture des compétences prioritaires
    let coverageScore = 0;
    let criticalSkillsBonus = 0;
    
    skillsToCover.forEach(skillId => {
      const need = coverageNeeds[skillId];
      if (need) {
        const remainingNeed = Math.max(0, need.requiredPeriods - need.coveredPeriods);
        const urgency = remainingNeed > 0 ? remainingNeed / need.requiredPeriods : 0; // 0 à 1
        
        // Score de base pour la couverture
        coverageScore += need.priority * urgency * 3;
        
        // Bonus supplémentaire pour les compétences très critiques (déficit > 50%)
        if (urgency > 0.5) {
          criticalSkillsBonus += need.priority * urgency * 2;
          console.log(`    🚨 Compétence critique détectée: ${need.skill.name} (urgence: ${(urgency * 100).toFixed(0)}%)`);
        }
      }
    });
    
    // 2. Score d'équilibrage de charge (NOUVEAU: BASÉ SUR L'OBJECTIF ANNUEL)
    // Utiliser la priorité d'équilibrage annuel au lieu de l'équilibrage sur la période
    let loadBalance = techStats.priorityScore || 200; // Utiliser le score de priorité annuel
    
    // Bonus/Pénalités supplémentaires selon le déficit/excès annuel
    if (techStats.isUnderTarget && techStats.periodDeficit >= 3) {
      // BONUS ÉNORME pour rattraper un gros déficit de période
      loadBalance += techStats.periodDeficit * 300; // Augmenté de 150 à 300
      console.log(`    📈 ÉQUILIBRAGE PÉRIODE: ${techStats.technician?.firstName || 'Tech'} GROS DÉFICIT (${techStats.periodDeficit} manquantes, bonus: +${techStats.periodDeficit * 300})`);
    } else if (techStats.isUnderTarget && techStats.periodDeficit >= 2) {
      // BONUS très important pour déficit modéré
      loadBalance += techStats.periodDeficit * 250; // Nouveau niveau
      console.log(`    📈 ÉQUILIBRAGE PÉRIODE: ${techStats.technician?.firstName || 'Tech'} DÉFICIT MODÉRÉ (${techStats.periodDeficit} manquantes, bonus: +${techStats.periodDeficit * 250})`);
    } else if (techStats.isUnderTarget) {
      // BONUS important pour rattraper un déficit de période
      loadBalance += techStats.periodDeficit * 200; // Augmenté de 100 à 200
      console.log(`    📈 ÉQUILIBRAGE PÉRIODE: ${techStats.technician?.firstName || 'Tech'} déficit (${techStats.periodDeficit} manquantes, bonus: +${techStats.periodDeficit * 200})`);
    } else if (techStats.isOverTarget && techStats.periodExcess >= 2) {
      // PÉNALITÉ MASSIVE pour éviter d'aggraver un gros excès
      loadBalance -= techStats.periodExcess * 400; // Augmenté de 200 à 400
      console.log(`    📉 ÉQUILIBRAGE PÉRIODE: ${techStats.technician?.firstName || 'Tech'} GROS EXCÈS (${techStats.periodExcess} en trop, pénalité: -${techStats.periodExcess * 400})`);
    } else if (techStats.isOverTarget) {
      // PÉNALITÉ importante pour éviter d'aggraver un excès
      loadBalance -= techStats.periodExcess * 250; // Augmenté de 120 à 250
      console.log(`    📉 ÉQUILIBRAGE PÉRIODE: ${techStats.technician?.firstName || 'Tech'} excès (${techStats.periodExcess} en trop, pénalité: -${techStats.periodExcess * 250})`);
    } else {
      console.log(`    ⚖️ ÉQUILIBRAGE PÉRIODE: ${techStats.technician?.firstName || 'Tech'} dans l'objectif de période`);
    }
    
    // 🔧 CORRECTION : BONUS D'ÉQUITÉ basé sur l'écart-type des primes (détection objective)
    let equityBonus = 0;
    const technicianName = `${techStats.technician?.firstName || ''} ${techStats.technician?.lastName || ''}`.trim();
    
    // Calculer les primes totales actuelles de chaque technicien
    const allTechPrimes = {};
    formData.selectedTechnicians.forEach(tId => {
      const stat = allTechStats[tId];
      allTechPrimes[tId] = stat ? ((stat.currentPrimes || 0) + (stat.tempPrimes || 0)) : 0;
    });
    
    const currentTechPrimes = allTechPrimes[techId] || 0;
    const allPrimesValues = Object.values(allTechPrimes);
    const averagePrimes = allPrimesValues.length > 0 ? 
      allPrimesValues.reduce((sum, p) => sum + p, 0) / allPrimesValues.length : 
      currentTechPrimes;
    
    // Calculer le déficit/excès en euros par rapport à la moyenne
    const primeDeficit = averagePrimes - currentTechPrimes;
    const estimatedPrime = 478; // Prime standard
    
    if (primeDeficit >= 2 * estimatedPrime) {
      // Technicien très sous-payé (≥ 956€ de déficit = 2+ astreintes de retard)
      equityBonus = 400;
      console.log(`    💰 ÉQUITÉ PRIMES: ${technicianName} très sous-payé (${currentTechPrimes}€ vs moyenne ${averagePrimes.toFixed(0)}€, déficit: ${primeDeficit.toFixed(0)}€) → BONUS +${equityBonus}`);
    } else if (primeDeficit >= estimatedPrime) {
      // Technicien sous-payé (≥ 478€ de déficit = 1+ astreinte de retard)
      equityBonus = 250;
      console.log(`    💰 ÉQUITÉ PRIMES: ${technicianName} sous-payé (${currentTechPrimes}€ vs moyenne ${averagePrimes.toFixed(0)}€, déficit: ${primeDeficit.toFixed(0)}€) → BONUS +${equityBonus}`);
    } else if (primeDeficit >= estimatedPrime * 0.5) {
      // Technicien légèrement sous-payé (≥ 239€ de déficit)
      equityBonus = 100;
      console.log(`    💰 ÉQUITÉ PRIMES: ${technicianName} légèrement sous-payé (${currentTechPrimes}€ vs moyenne ${averagePrimes.toFixed(0)}€, déficit: ${primeDeficit.toFixed(0)}€) → BONUS +${equityBonus}`);
    } else if (primeDeficit <= -2 * estimatedPrime) {
      // Technicien sur-payé : pénalité pour éviter d'aggraver le déséquilibre
      equityBonus = -200;
      console.log(`    💰 ÉQUITÉ PRIMES: ${technicianName} sur-payé (${currentTechPrimes}€ vs moyenne ${averagePrimes.toFixed(0)}€, excès: ${Math.abs(primeDeficit).toFixed(0)}€) → PÉNALITÉ ${equityBonus}`);
    }
    
    // Appliquer le bonus d'équité au loadBalance
    loadBalance += equityBonus;
    
    // 3. Score de compétences (RÉDUIT pour privilégier l'équilibrage)
    let competencyScore = 0;
    
    // Score de base équitable mais réduit : chaque compétence pertinente compte pareil
    competencyScore = skillsToCover.length * 10; // Réduit de 15 à 10
    
    // 4. Gestion de la redondance (TRÈS MODÉRÉE pour ne pas bloquer l'équilibrage)
    let redundancyAdjustment = 0;
    if (strategy.alreadyCoveredSkills && strategy.alreadyCoveredSkills.length > 0) {
      const redundantSkills = skillsToCover.filter(skillId => 
        strategy.alreadyCoveredSkills.includes(skillId)
      );
      const newSkills = skillsToCover.filter(skillId => 
        !strategy.alreadyCoveredSkills.includes(skillId)
      );
      
      // Ajustements très modérés pour ne pas éliminer l'équilibrage
      redundancyAdjustment -= redundantSkills.length * 5; // Réduit de 10 à 5
      redundancyAdjustment += newSkills.length * 8; // Réduit de 15 à 8
      
      console.log(`    📊 Compétences: ${redundantSkills.length} redondantes (${-redundantSkills.length * 5}), ${newSkills.length} nouvelles (+${newSkills.length * 8})`);
    }
    
    // 5. Bonus pour repos depuis la dernière affectation (MAINTENU)
    let restBonus = 0;
    if (techStats.lastAssignmentDate && strategy.periodStartTime) {
      // Vérifier que les dates sont valides avant de les utiliser
      const lastAssignmentDateObj = new Date(techStats.lastAssignmentDate);
      const periodStartTimeObj = new Date(strategy.periodStartTime);
      
      if (!isNaN(lastAssignmentDateObj.getTime()) && !isNaN(periodStartTimeObj.getTime())) {
        // Utiliser le même calcul que pour la validation
        const restCalculation = calculatePeriodsBetweenDates(lastAssignmentDateObj, periodStartTimeObj);
        
        if (restCalculation.completePeriods >= 6) {
          // Bonus massif pour technicien très reposé (6+ périodes = 6+ semaines)
          restBonus = 50;
        } else if (restCalculation.completePeriods >= 4) {
          // Bonus important pour technicien bien reposé (4-5 périodes = 4-5 semaines)
          restBonus = 30;
        } else if (restCalculation.completePeriods >= 2) {
          // Bonus modéré pour repos minimum respecté (2-3 périodes = 2-3 semaines)
          restBonus = 10;
        }
        
        console.log(`    💤 Repos: ${restCalculation.daysDifference} jours = ${restCalculation.completePeriods} période(s) (bonus: +${restBonus})`);
      } else {
        console.log(`    💤 Repos: Dates invalides détectées, aucun bonus appliqué`);
      }
    } else if (!techStats.lastAssignmentDate) {
      // Bonus pour technicien sans affectation récente
      restBonus = 60;
      console.log(`    💤 Repos: Aucune affectation récente (bonus: +${restBonus})`);
    }
    
    // 6. Bonus pour compétences manquantes critiques sur cette période (MAINTENU)
    let missingCriticalBonus = 0;
    if (strategy.criticalMissingSkills && strategy.criticalMissingSkills.length > 0) {
      const criticalSkillsCovered = skillsToCover.filter(skillId => 
        strategy.criticalMissingSkills.includes(skillId)
      );
      
      // BONUS MASSIF pour couvrir des compétences critiques manquantes
      missingCriticalBonus = criticalSkillsCovered.length * 100;
      
      if (criticalSkillsCovered.length > 0) {
        console.log(`    🎯 CRITIQUES manquantes couvertes: ${criticalSkillsCovered.length} (bonus: +${missingCriticalBonus})`);
      }
    } else if (strategy.alreadyCoveredSkills) {
      // Ancien code pour compatibilité
      const criticalMissingSkills = formData.selectedSkills.filter(skillId => {
        const skill = data.skills.find(s => s.id === skillId);
        const isCritical = skill && skill.coverageIndex >= 80;
        const notYetCovered = !strategy.alreadyCoveredSkills.includes(skillId);
        const technicianHasIt = skillsToCover.includes(skillId);
        return isCritical && notYetCovered && technicianHasIt;
      });
      
      missingCriticalBonus = criticalMissingSkills.length * 100;
      
      if (criticalMissingSkills.length > 0) {
        console.log(`    🎯 Compétences critiques manquantes couvertes: ${criticalMissingSkills.length}`);
      }
    }
    
    // Score final pondéré selon la stratégie (ÉQUILIBRAGE ULTRA-PRIORITAIRE)
    const strategyPrioritizeCoverage = strategy.prioritizeCoverage;
    const strategyBalanceLoad = strategy.balanceLoad;
    const strategyTargetCritical = strategy.targetCriticalSkills;
    
    // Appliquer l'ajustement de redondance au score de compétences
    const finalCompetencyScore = competencyScore + redundancyAdjustment;
    
    if (strategyPrioritizeCoverage) {
      // Phase 1: Équilibrage ULTRA-PRIORITAIRE dès le début (modifié)
      score = (coverageScore + criticalSkillsBonus + missingCriticalBonus) * 0.15 + // Réduit de 0.3 à 0.15
              loadBalance * 0.8 + // Augmenté de 0.6 à 0.8 (PRIORITÉ ABSOLUE)
              finalCompetencyScore * 0.03 + // Réduit de 0.05 à 0.03
              restBonus * 0.02; // Réduit de 0.05 à 0.02
    } else if (strategyBalanceLoad) {
      // Phase 2: Équilibrage ULTRA-MASSIF 
      score = (coverageScore + criticalSkillsBonus + missingCriticalBonus) * 0.1 + // Réduit de 0.15 à 0.1
              loadBalance * 0.85 + // Augmenté de 0.75 à 0.85 (PRIORITÉ ULTIME)
              finalCompetencyScore * 0.03 + // Réduit de 0.05 à 0.03
              restBonus * 0.02; // Réduit de 0.05 à 0.02
    } else if (strategyTargetCritical) {
      // Phase 3: Critiques + équilibrage ultra-fort (modifié)
      score = (coverageScore + criticalSkillsBonus * 2 + missingCriticalBonus) * 0.25 + // Réduit de 0.5 à 0.25
              loadBalance * 0.7 + // Augmenté de 0.4 à 0.7
              finalCompetencyScore * 0.03 + // Réduit de 0.05 à 0.03
              restBonus * 0.02; // Réduit de 0.05 à 0.02
    } else {
      // Mode par défaut: équilibrage ULTRA-PRIORITAIRE (modifié)
      score = (coverageScore + criticalSkillsBonus + missingCriticalBonus) * 0.12 + // Réduit de 0.25 à 0.12
              loadBalance * 0.83 + // Augmenté de 0.65 à 0.83
              finalCompetencyScore * 0.03 + // Réduit de 0.05 à 0.03
              restBonus * 0.02; // Réduit de 0.05 à 0.02
    }
    
    console.log(`    🎯 SCORE FINAL: ${Math.max(0, score).toFixed(1)} (équilibrage: ${loadBalance.toFixed(1)}, couverture: ${coverageScore.toFixed(1)}, compétences: ${finalCompetencyScore.toFixed(1)})`);
    
    // DEBUG FINAL pour Steven HOUY
    if (isHouy) {
      console.log(`\n🔍 HOUY DEBUG - SCORE FINAL:`);
      console.log(`   🎯 Score total: ${Math.max(0, score).toFixed(2)}`);
      console.log(`   ⚖️ Équilibrage: ${loadBalance.toFixed(2)}`);
      console.log(`   📊 Couverture: ${coverageScore.toFixed(2)}`);
      console.log(`   🛠️ Compétences: ${finalCompetencyScore.toFixed(2)}`);
      console.log(`   💤 Repos: ${restBonus.toFixed(2)}`);
      console.log(`   🎯 Critiques: ${missingCriticalBonus.toFixed(2)}`);
    }
    
    return {
      totalScore: Math.max(0, score),
      coverageScore,
      criticalSkillsBonus,
      loadBalance,
      competencyScore: finalCompetencyScore,
      restBonus,
      missingCriticalBonus,
      hasConstraintViolations: false
    };
  };

  const calculateSolutionScore = (assignments, coverageIssues, conflicts) => {
    let score = 0;
    
    // Pénalités pour conflits (très graves)
    score += conflicts.length * 1000;
    
    // Pénalités pour problèmes de couverture (pondérées par le déficit)
    coverageIssues.forEach(issue => {
      const deficitRatio = issue.deficit / issue.requiredPeriods;
      score += deficitRatio * 100 * (issue.skill.coverageIndex / 100); // Plus l'indice est élevé, plus la pénalité est forte
    });
    
    // Bonus pour le nombre d'affectations réussies
    score -= assignments.length * 2;
    
    // 🔧 CORRECTION MAJEURE: Pénalité MASSIVE pour l'écart min-max (équité perçue)
    const gapAnalysis = calculatePrimesMinMaxGap(assignments, {}, formData.selectedTechnicians);
    
    if (gapAnalysis && gapAnalysis.gap > 0) {
      const estimatedPrime = 478;
      
      // PÉNALITÉ EXPONENTIELLE pour l'écart min-max (ce que voient les techniciens)
      let minMaxPenalty = 0;
      
      if (gapAnalysis.gap >= 4 * estimatedPrime) {
        // Écart ≥ 4 astreintes : CATASTROPHIQUE
        minMaxPenalty = 1000 + (gapAnalysis.periodsGap - 4) * 500;
        console.log(`🚨 ÉQUITÉ CRITIQUE: Écart ${gapAnalysis.periodsGap.toFixed(1)} astreintes (${gapAnalysis.gap}€) → PÉNALITÉ MASSIVE ${minMaxPenalty}`);
      } else if (gapAnalysis.gap >= 3 * estimatedPrime) {
        // Écart ≥ 3 astreintes : TRÈS GRAVE
        minMaxPenalty = 500 + (gapAnalysis.periodsGap - 3) * 200;
        console.log(`⚠️ ÉQUITÉ GRAVE: Écart ${gapAnalysis.periodsGap.toFixed(1)} astreintes (${gapAnalysis.gap}€) → PÉNALITÉ ${minMaxPenalty}`);
      } else if (gapAnalysis.gap >= 2 * estimatedPrime) {
        // Écart ≥ 2 astreintes : PROBLÉMATIQUE
        minMaxPenalty = 200 + (gapAnalysis.periodsGap - 2) * 100;
        console.log(`⚠️ ÉQUITÉ PROBLÉMATIQUE: Écart ${gapAnalysis.periodsGap.toFixed(1)} astreintes (${gapAnalysis.gap}€) → PÉNALITÉ ${minMaxPenalty}`);
      } else if (gapAnalysis.gap >= 1 * estimatedPrime) {
        // Écart ≥ 1 astreinte : LIMITE ACCEPTABLE
        minMaxPenalty = 50 + (gapAnalysis.periodsGap - 1) * 50;
        console.log(`⚖️ ÉQUITÉ LIMITE: Écart ${gapAnalysis.periodsGap.toFixed(1)} astreintes (${gapAnalysis.gap}€) → PÉNALITÉ ${minMaxPenalty}`);
      } else {
        // Écart < 1 astreinte : ACCEPTABLE
        minMaxPenalty = gapAnalysis.periodsGap * 10;
        console.log(`✅ ÉQUITÉ ACCEPTABLE: Écart ${gapAnalysis.periodsGap.toFixed(1)} astreintes (${gapAnalysis.gap}€) → PÉNALITÉ légère ${minMaxPenalty}`);
      }
      
      score += minMaxPenalty;
      
      // Aussi calculer l'écart-type pour information
      const primesStdDev = calculatePrimesStandardDeviation(assignments, {}, formData.selectedTechnicians);
      console.log(`📊 Info écart-type: ${primesStdDev.toFixed(0)}€ | PRIORITÉ: Écart min-max ${gapAnalysis.gap}€`);
    }
    
    return score;
  };

  const getPlanningPeriods = () => {
    const periods = [];
    
    // Trouver le premier vendredi dans la plage de dates
    let currentDate = new Date(formData.startDate);
    
    // Aller au premier vendredi à partir de la date de début
    while (currentDate.getDay() !== 5) { // 5 = vendredi
      currentDate = addDays(currentDate, 1);
    }
    
    // Si on a dépassé la date de fin, aucune période d'astreinte ne peut commencer
    if (currentDate > formData.endDate) {
      return periods;
    }

    // Générer des périodes d'astreinte de vendredi à jeudi (7 jours)
    while (currentDate <= formData.endDate) {
      const startDate = new Date(currentDate);
      startDate.setHours(18, 0, 0, 0); // Vendredi 18h
      
      const endDate = new Date(currentDate);
      endDate.setDate(endDate.getDate() + 6); // Jeudi suivant
      endDate.setHours(18, 0, 0, 0); // Jeudi 18h
      
      // Vérifier si la période chevauche avec la plage demandée
      if (startDate <= formData.endDate && endDate >= formData.startDate) {
        // Vérifier s'il y a des jours fériés dans cette période d'astreinte
        const periodDays = eachDayOfInterval({ start: startDate, end: endDate });
        const hasHoliday = periodDays.some(day => isHoliday(day, data.holidays));
        
        periods.push({
          id: `period_${periods.length}`, // CORRECTION: Ajouter un ID à chaque période
          startDate,
          endDate,
          isWeekend: true, // Une période d'astreinte complète inclut toujours le weekend
          isHoliday: hasHoliday,
          // Suppression de requiredSkills - chaque technicien couvrira toutes ses compétences
          weekNumber: getWeek(startDate) // Utiliser getWeek de date-fns pour le numéro de semaine ISO
        });
      }
      
      // Passer au vendredi suivant
      currentDate = addDays(currentDate, 7);
    }

    console.log('=== PÉRIODES D\'ASTREINTE GÉNÉRÉES ===');
    periods.forEach((period, index) => {
      console.log(`Période ${index + 1} (${period.id}):`, 
        format(period.startDate, 'dd/MM/yyyy HH:mm', { locale: fr }), 
        '→', 
        format(period.endDate, 'dd/MM/yyyy HH:mm', { locale: fr }),
        period.isHoliday ? '(avec jour férié)' : ''
      );
    });

    return periods;
  };

  const calculateTechnicianStats = async () => {
    const stats = {};
    
    // NOUVEAU: Utiliser l'année de la planification, pas l'année courante
    const planningYear = formData.startDate.getFullYear();
    console.log(`📅 Année de planification détectée: ${planningYear}`);
    
    // NOUVEAU: Configuration de l'objectif proportionnel à la période de planification
    const ANNUAL_TARGET_MIN = 9; // Minimum souhaité par an
    const ANNUAL_TARGET_MAX = 11; // Maximum souhaité par an
    const ANNUAL_TARGET_OPTIMAL = 10; // Objectif optimal annuel
    
    // Calculer la proportion de l'année couverte par la période de planification
    const yearStart = new Date(planningYear, 0, 1); // 1er janvier de l'année de planification
    const yearEnd = new Date(planningYear, 11, 31); // 31 décembre de l'année de planification
    const planningStart = formData.startDate;
    const planningEnd = formData.endDate;
    
    const yearDuration = yearEnd - yearStart;
    const planningDuration = planningEnd - planningStart;
    const yearCoverage = Math.min(1, planningDuration / yearDuration); // Max 100%
    
    // Calculer le nombre de périodes d'astreintes dans la planification
    const periodsInPlanning = Math.ceil(planningDuration / (7 * 24 * 60 * 60 * 1000)); // Nombre de semaines
    
    // NOUVEAU: Objectif d'équité ultra-strict pour les semestres
    // Pour un semestre (~26 semaines), l'écart doit être de 1 astreinte maximum
    const PERIOD_TARGET_OPTIMAL = Math.round(ANNUAL_TARGET_OPTIMAL * yearCoverage);
    let PERIOD_TARGET_MIN, PERIOD_TARGET_MAX;
    
    if (periodsInPlanning >= 20) { // Semestre ou plus (≥ 20 semaines)
      // Équité ultra-stricte : écart maximum de 1
      PERIOD_TARGET_MIN = PERIOD_TARGET_OPTIMAL;
      PERIOD_TARGET_MAX = PERIOD_TARGET_OPTIMAL + 1;
      console.log(`🎯 Mode ÉQUITÉ ULTRA-STRICTE activé (${periodsInPlanning} semaines)`);
    } else if (periodsInPlanning >= 10) { // Trimestre (≥ 10 semaines)
      // Équité stricte : écart maximum de 2
      PERIOD_TARGET_MIN = Math.max(0, PERIOD_TARGET_OPTIMAL - 1);
      PERIOD_TARGET_MAX = PERIOD_TARGET_OPTIMAL + 1;
      console.log(`🎯 Mode ÉQUITÉ STRICTE activé (${periodsInPlanning} semaines)`);
    } else { // Courte période (< 10 semaines)
      // Équité normale
      PERIOD_TARGET_MIN = Math.round(ANNUAL_TARGET_MIN * yearCoverage);
      PERIOD_TARGET_MAX = Math.round(ANNUAL_TARGET_MAX * yearCoverage);
      console.log(`🎯 Mode ÉQUITÉ NORMALE activé (${periodsInPlanning} semaines)`);
    }
    
    console.log(`📊 Période de planification: ${format(planningStart, 'dd/MM/yyyy', { locale: fr })} → ${format(planningEnd, 'dd/MM/yyyy', { locale: fr })}`);
    console.log(`📊 Durée: ${periodsInPlanning} semaines (${(yearCoverage * 100).toFixed(1)}% de l'année)`);
    console.log(`📊 Objectif ajusté pour la période: ${PERIOD_TARGET_MIN}-${PERIOD_TARGET_MAX} astreintes/technicien (optimal: ${PERIOD_TARGET_OPTIMAL})`);
    console.log(`📊 Objectif annuel de référence: ${ANNUAL_TARGET_MIN}-${ANNUAL_TARGET_MAX} astreintes/technicien (optimal: ${ANNUAL_TARGET_OPTIMAL})`);
    console.log(`⚖️ Équité requise: écart maximum ${PERIOD_TARGET_MAX - PERIOD_TARGET_MIN} astreinte(s) entre techniciens`);
    
    // Initialiser les stats pour chaque technicien sélectionné
    formData.selectedTechnicians.forEach(techId => {
      const tech = data.technicians.find(t => t.id === techId);
      stats[techId] = {
        technician: tech,
        currentAssignments: 0,
        currentPrimes: 0,
        tempAssignments: 0,
        tempPrimes: 0,
        lastAssignmentDate: null,
        skills: tech.skills || [],
        // NOUVEAU: Métriques d'équilibrage pour la période
        periodTarget: PERIOD_TARGET_OPTIMAL,
        periodDeficit: 0, // Sera calculé après
        periodExcess: 0,
        isUnderTarget: false,
        isOverTarget: false,
        priorityScore: 0 // Score de priorité pour l'équilibrage de la période
      };
    });

    // Calculer les stats actuelles pour l'année de planification UNIQUEMENT
    data.existingOnCalls.forEach(onCall => {
      const year = new Date(onCall.startDate).getFullYear();
      if (year === planningYear && stats[onCall.technicianId]) {
        stats[onCall.technicianId].currentAssignments++;
        
        // Calculer la prime
        const hasHolidayPeriod = isHoliday(new Date(onCall.startDate), data.holidays);
        stats[onCall.technicianId].currentPrimes += hasHolidayPeriod ? 600 : 478;
        
        // Mettre à jour la dernière affectation
        const assignmentDate = new Date(onCall.endDate);
        if (!stats[onCall.technicianId].lastAssignmentDate || 
            assignmentDate > stats[onCall.technicianId].lastAssignmentDate) {
          stats[onCall.technicianId].lastAssignmentDate = assignmentDate;
        }
      }
    });

    // NOUVEAU: Calculer les métriques d'équilibrage pour la période
    console.log('\n📊 === ANALYSE ÉQUILIBRAGE POUR LA PÉRIODE ===');
    
    Object.values(stats).forEach(stat => {
      const currentTotal = stat.currentAssignments + stat.tempAssignments;
      const deficit = Math.max(0, stat.periodTarget - currentTotal);
      const excess = Math.max(0, currentTotal - stat.periodTarget);
      
      stat.periodDeficit = deficit;
      stat.periodExcess = excess;
      stat.isUnderTarget = deficit > 0;
      stat.isOverTarget = excess > 0;
      
      // NOUVEAU: Score de priorité adaptatif selon le mode d'équité
      if (periodsInPlanning >= 20) { // Mode ultra-strict pour semestres
        if (deficit >= 2) {
          stat.priorityScore = 5000 + deficit * 500; // Priorité absolue
        } else if (deficit >= 1) {
          stat.priorityScore = 3000 + deficit * 300; // Priorité ultra-maximale
        } else if (excess === 0) {
          stat.priorityScore = 1000; // Équilibré = priorité élevée
        } else if (excess === 1) {
          stat.priorityScore = 200; // Légèrement défavorisé mais toléré
        } else {
          stat.priorityScore = 10; // Fortement évité (écart > 1)
        }
      } else { // Mode normal pour périodes courtes
        if (deficit >= 3) {
          stat.priorityScore = 2000 + deficit * 200; // Priorité ultra-maximale
        } else if (deficit >= 2) {
          stat.priorityScore = 1500 + deficit * 150; // Priorité très élevée
        } else if (deficit >= 1) {
          stat.priorityScore = 1000 + deficit * 100; // Priorité élevée
        } else if (excess === 0) {
          stat.priorityScore = 500; // Équilibré
        } else if (excess === 1) {
          stat.priorityScore = 100 - excess * 50; // Légèrement défavorisé
        } else if (excess === 2) {
          stat.priorityScore = 50 - excess * 30; // Défavorisé
        } else {
          stat.priorityScore = 10 - excess * 20; // Fortement évité
        }
      }
      
      const status = stat.isUnderTarget ? `📉 DÉFICIT ${deficit}` : 
                    stat.isOverTarget ? `📈 EXCÈS ${excess}` : '⚖️ ÉQUILIBRÉ';
      
      console.log(`   ${stat.technician.firstName} ${stat.technician.lastName}: ${currentTotal}/${stat.periodTarget} - ${status} (priorité: ${stat.priorityScore})`);
    });
    
    // Analyser la distribution globale
    const allCurrentTotals = Object.values(stats).map(s => s.currentAssignments + s.tempAssignments);
    const underTargetCount = Object.values(stats).filter(s => s.isUnderTarget).length;
    const overTargetCount = Object.values(stats).filter(s => s.isOverTarget).length;
    const balancedCount = Object.values(stats).filter(s => !s.isUnderTarget && !s.isOverTarget).length;
    
    console.log(`\n📈 Répartition actuelle:`);
    console.log(`   🔻 Sous-objectif: ${underTargetCount} techniciens`);
    console.log(`   ⚖️ Équilibrés: ${balancedCount} techniciens`);
    console.log(`   🔺 Sur-objectif: ${overTargetCount} techniciens`);
    console.log(`   📊 Écart min-max: ${Math.min(...allCurrentTotals)}-${Math.max(...allCurrentTotals)}`);
    
    console.log('=== FIN ANALYSE ÉQUILIBRAGE POUR LA PÉRIODE ===\n');

    return stats;
  };

  const calculatePlanningStats = (assignments, technicianStats) => {
    const stats = {
      totalAssignments: assignments.length,
      technicianDistribution: {},
      primeDistribution: {},
      equilibriumScore: 0
    };

    // Calculer d'abord les nouvelles affectations par technicien à partir des assignments réels
    const newAssignmentsByTech = {};
    const newPrimesByTech = {};
    
    assignments.forEach(assignment => {
      const techId = assignment.technicianId;
      if (!newAssignmentsByTech[techId]) {
        newAssignmentsByTech[techId] = 0;
        newPrimesByTech[techId] = 0;
      }
      newAssignmentsByTech[techId]++;
      newPrimesByTech[techId] += assignment.estimatedPrime;
    });

    // Construire la distribution pour tous les techniciens sélectionnés
    formData.selectedTechnicians.forEach(techId => {
      const tech = data.technicians.find(t => t.id === techId);
      const stat = technicianStats[techId];
      
      if (tech && stat) {
        const assignmentsAdded = newAssignmentsByTech[techId] || 0;
        const primesAdded = newPrimesByTech[techId] || 0;
        const assignmentsBefore = stat.currentAssignments;
        const primesBefore = stat.currentPrimes;
        
        stats.technicianDistribution[techId] = {
          technician: tech,
          assignmentsBefore: assignmentsBefore,
          assignmentsAdded: assignmentsAdded,
          assignmentsTotal: assignmentsBefore + assignmentsAdded,
          primesBefore: primesBefore,
          primesAdded: primesAdded,
          primesTotal: primesBefore + primesAdded
        };
      }
    });

    // Calculer l'écart type des primes TOTALES pour mesurer l'équilibre
    const allTotalPrimes = Object.values(stats.technicianDistribution).map(d => d.primesTotal);
    if (allTotalPrimes.length > 1) {
      const averagePrimes = allTotalPrimes.reduce((sum, p) => sum + p, 0) / allTotalPrimes.length;
      const variance = allTotalPrimes.reduce((sum, p) => sum + Math.pow(p - averagePrimes, 2), 0) / allTotalPrimes.length;
      stats.equilibriumScore = Math.sqrt(variance);
    }

    return stats;
  };

  const validateAssignments = (assignments) => {
    const conflicts = [];
    
    // Vérifier les chevauchements (seulement pour le même technicien sur des périodes différentes)
    for (let i = 0; i < assignments.length; i++) {
      for (let j = i + 1; j < assignments.length; j++) {
        const a1 = assignments[i];
        const a2 = assignments[j];
        
        // Conflit seulement si c'est le même technicien ET des périodes qui se chevauchent
        // mais qui ne sont PAS exactement la même période (car on permet plusieurs techs sur la même période)
        if (a1.technicianId === a2.technicianId) {
          const sameExactPeriod = a1.startDate.getTime() === a2.startDate.getTime() && 
                                 a1.endDate.getTime() === a2.endDate.getTime();
          
          if (!sameExactPeriod && a1.startDate < a2.endDate && a2.startDate < a1.endDate) {
            conflicts.push({
              type: 'overlap',
              message: `Chevauchement détecté pour ${a1.technician.firstName} ${a1.technician.lastName}`,
              assignments: [a1, a2]
            });
          }
        }
      }
    }

    return conflicts;
  };

  const applyPlanning = async () => {
    if (!planning || planning.conflicts.length > 0) return;

    setLoading(true);
    try {
      // Créer les astreintes dans la base de données
      for (const assignment of planning.assignments) {
        await addOnCall({
          technicianId: assignment.technicianId,
          startDate: assignment.startDate.toISOString(),
          endDate: assignment.endDate.toISOString(),
          skills: assignment.skills,
          isWeekend: assignment.isWeekend,
          createdBy: 'auto-planning',
          createdAt: new Date().toISOString()
        });
      }

      onPlanningGenerated(planning.assignments);
      setStep('completed');
    } catch (error) {
      console.error('Erreur lors de l\'application du planning:', error);
      setError('Erreur lors de l\'enregistrement: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderSetupStep = () => {
    // Debug pour diagnostiquer le problème
    console.log('🔍 DEBUG formData:', {
      aiProvider: formData.aiProvider,
      aiModel: formData.aiModel
    });
    
    return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <DatePicker
          label="Date de début"
          value={formData.startDate}
          onChange={(date) => handleFormChange('startDate', date)}
          renderInput={(params) => <TextField {...params} fullWidth />}
          minDate={new Date()}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DatePicker
          label="Date de fin"
          value={formData.endDate}
          onChange={(date) => handleFormChange('endDate', date)}
          renderInput={(params) => <TextField {...params} fullWidth />}
          minDate={formData.startDate || new Date()}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Compétences requises</InputLabel>
          <Select
            multiple
            value={formData.selectedSkills}
            onChange={(e) => handleFormChange('selectedSkills', e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((skillId) => {
                  const skill = data.skills.find(s => s.id === skillId);
                  return (
                    <Chip
                      key={skillId}
                      label={skill?.name || skillId}
                      size="small"
                      sx={{ backgroundColor: skill?.color + '20' }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {[...data.skills]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((skill) => (
                <MenuItem key={skill.id} value={skill.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: skill.color,
                        borderRadius: '50%'
                      }}
                    />
                    {skill.name}
                  </Box>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Techniciens disponibles</InputLabel>
          <Select
            multiple
            value={formData.selectedTechnicians}
            onChange={(e) => handleFormChange('selectedTechnicians', e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((techId) => {
                  const tech = data.technicians.find(t => t.id === techId);
                  return (
                    <Chip
                      key={techId}
                      label={tech ? `${tech.firstName} ${tech.lastName}` : techId}
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
          >
            {data.technicians.map((tech) => (
              <MenuItem key={tech.id} value={tech.id}>
                <PersonIcon sx={{ mr: 1 }} />
                {tech.firstName} {tech.lastName}
                <Box sx={{ ml: 1, display: 'flex', gap: 0.5 }}>
                  {(tech.skills || []).map(skillId => {
                    const skill = data.skills.find(s => s.id === skillId);
                    return skill ? (
                      <Box
                        key={skillId}
                        sx={{
                          width: 8,
                          height: 8,
                          backgroundColor: skill.color,
                          borderRadius: '50%'
                        }}
                      />
                    ) : null;
                  })}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Techniciens maximum par période d'astreinte"
          value={formData.maxTechniciansPerPeriod}
          onChange={(e) => handleFormChange('maxTechniciansPerPeriod', Math.min(5, Math.max(1, parseInt(e.target.value) || 4)))}
          inputProps={{ min: 1, max: 5 }}
          helperText="Min: 1, Max: 5 (recommandé: 3-4)"
        />
      </Grid>
    </Grid>
    );
  };

  const renderPreviewStep = () => {
    if (!planning) return null;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Aperçu du planning généré
        </Typography>

        {/* Résumé */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Planification réussie avec la stratégie : <strong>{planning.strategy}</strong>
          </Typography>
          <Typography variant="body2">
            {planning.assignments.length} astreintes planifiées avec un écart de{' '}
            {(() => {
              // Calculer l'écart d'équité basé sur le NOMBRE d'astreintes (pas les primes)
              // Utiliser summary.technicianDistribution qui contient les vraies stats
              if (planning.summary?.technicianDistribution) {
                const distribution = Object.values(planning.summary.technicianDistribution);
                // Utiliser les totaux annuels (existants + nouveaux) pour l'écart affiché,
                // cohérent avec ce que le Manager IA valide
                const counts = distribution.map(d => d.assignmentsTotal ?? d.assignmentsAdded ?? 0);
                
                if (counts.length === 0) {
                  return '0 astreinte (0€)';
                }
                
                const minCount = Math.min(...counts);
                const maxCount = Math.max(...counts);
                const ecartAstreintes = maxCount - minCount;
                const ecartEuros = ecartAstreintes * 478;
                return `${ecartAstreintes} astreinte${ecartAstreintes > 1 ? 's' : ''} (${ecartEuros}€)`;
              }
              
              // Fallback: compter depuis assignments
              const countsPerTechnician = {};
              planning.assignments.forEach(assignment => {
                const techId = assignment.technician_id;
                countsPerTechnician[techId] = (countsPerTechnician[techId] || 0) + 1;
              });
              
              const counts = Object.values(countsPerTechnician);
              if (counts.length === 0) {
                return '0 astreinte (0€)';
              }
              
              const minCount = Math.min(...counts);
              const maxCount = Math.max(...counts);
              const ecartAstreintes = maxCount - minCount;
              const ecartEuros = ecartAstreintes * 478;
              return `${ecartAstreintes} astreinte${ecartAstreintes > 1 ? 's' : ''} (${ecartEuros}€)`;
            })()}
          </Typography>
          {planning.attempts && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Solution trouvée après {planning.attempts} tentative(s)
            </Typography>
          )}
          {planning.processingTime && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              ⏱️ Temps de traitement : <strong>{planning.processingTime.formattedDuration}</strong> 
              ({planning.processingTime.iterations} itérations)
            </Typography>
          )}
        </Alert>

        {/* Conflits */}
        {planning.conflicts.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Conflits détectés :</Typography>
            {planning.conflicts.map((conflict, index) => (
              <Typography key={index} variant="body2">
                • {conflict.message}
              </Typography>
            ))}
          </Alert>
        )}

        {/* Problèmes de couverture */}
        {planning.coverageIssues && planning.coverageIssues.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Problèmes de couverture détectés :
            </Typography>
            {planning.coverageIssues.map((issue, index) => {
              // Debug temporaire
              console.log('Issue détectée:', issue);
              console.log('uncoveredPeriods présent:', !!issue.uncoveredPeriods);
              console.log('Nombre de périodes non couvertes:', issue.uncoveredPeriods?.length || 0);
              
              return (
                <Box key={index} sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • <strong>{issue.skill.name}</strong> : {Math.round(issue.actualCoverage)}% 
                    (requis: {issue.requiredCoverage}%, déficit: {issue.deficit} période(s) d'astreinte)
                  </Typography>
                  {issue.uncoveredPeriods && issue.uncoveredPeriods.length > 0 && (
                    <Box sx={{ ml: 2, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        Périodes non couvertes :
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {issue.uncoveredPeriods.slice(0, 6).map((periodLabel, periodIndex) => (
                          <Chip
                            key={periodIndex}
                            label={periodLabel}
                            size="small"
                            variant="outlined"
                            color="warning"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                        {issue.uncoveredPeriods.length > 6 && (
                          <Chip
                            label={`+${issue.uncoveredPeriods.length - 6}`}
                            size="small"
                            variant="outlined"
                            color="warning"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                  {/* Debug temporaire - afficher même si pas de périodes */}
                  {(!issue.uncoveredPeriods || issue.uncoveredPeriods.length === 0) && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', ml: 2, mt: 1 }}>
                      ⚠️ Debug: Aucune période non couverte détectée pour {issue.skill.name}
                    </Typography>
                  )}
                </Box>
              );
            })}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Ces compétences ne respectent pas l'indice de couverture configuré.
            </Typography>
          </Alert>
        )}

        {/* Couverture satisfaisante */}
        {planning.coverageIssues && planning.coverageIssues.length === 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✓ Toutes les compétences respectent leur indice de couverture requis
          </Alert>
        )}

        {/* Affectations */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Affectations ({planning.assignments.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {(() => {
              // Grouper les affectations par période
              const assignmentsByPeriod = {};
              planning.assignments.forEach(assignment => {
                // 🔧 CORRECTION: Vérifier la validité des dates avant formatage
                if (!assignment.startDate || !assignment.endDate || 
                    isNaN(new Date(assignment.startDate).getTime()) || 
                    isNaN(new Date(assignment.endDate).getTime())) {
                  console.error('Date invalide dans assignment:', assignment);
                  return; // Ignorer cette affectation
                }
                
                const startDate = new Date(assignment.startDate);
                const endDate = new Date(assignment.endDate);
                const periodKey = `${format(startDate, 'dd/MM/yyyy HH:mm', { locale: fr })}-${format(endDate, 'dd/MM/yyyy HH:mm', { locale: fr })}`;
                
                if (!assignmentsByPeriod[periodKey]) {
                  assignmentsByPeriod[periodKey] = {
                    startDate: startDate,
                    endDate: endDate,
                    isHoliday: assignment.isHoliday,
                    weekNumber: assignment.weekNumber,
                    assignments: []
                  };
                }
                assignmentsByPeriod[periodKey].assignments.push(assignment);
              });

              // Trier les périodes chronologiquement
              const sortedPeriods = Object.entries(assignmentsByPeriod)
                .sort(([,a], [,b]) => new Date(a.startDate) - new Date(b.startDate));

              return (
                <List dense>
                  {sortedPeriods.map(([periodKey, periodData]) => (
                    <div key={periodKey}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              📅 {(() => {
                                try {
                                  return `${format(periodData.startDate, 'dd/MM/yyyy', { locale: fr })} - ${format(periodData.endDate, 'dd/MM/yyyy', { locale: fr })}`;
                                } catch (error) {
                                  console.error('Erreur formatage date:', error, periodData);
                                  return `${periodData.startDate} - ${periodData.endDate}`;
                                }
                              })()}
                              {periodData.isHoliday ? ' 🎉 (avec jour férié)' : ''}
                              {` • Semaine ${periodData.weekNumber || 'N/A'} • ${periodData.assignments.length} technicien(s)`}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {periodData.assignments.map((assignment, index) => (
                        <ListItem key={`${periodKey}-${index}`} sx={{ pl: 4 }}>
                          <ListItemIcon>
                            <PersonIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${assignment.technician.firstName} ${assignment.technician.lastName}`}
                            secondary={
                              `${assignment.estimatedPrime}€` +
                              (assignment.skills.length > 0 ? ` • Compétences: [${assignment.skills.map(skill => {
                                const skillId = typeof skill === 'object' ? skill.skillId : skill;
                                const level = typeof skill === 'object' ? skill.level || 1 : 1;
                                const skillName = data.skills.find(s => s.id === skillId)?.name || skillId;
                                return `${skillName} N${level}`;
                              }).join(', ')}]` : '')
                            }
                          />
                        </ListItem>
                      ))}
                    </div>
                  ))}
                </List>
              );
            })()}
          </AccordionDetails>
        </Accordion>

        {/* Distribution par technicien */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Répartition par technicien</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {Object.values(planning.summary.technicianDistribution).map((dist) => (
                <ListItem key={dist.technician.id}>
                  <ListItemText
                    primary={`${dist.technician.firstName} ${dist.technician.lastName}`}
                    secondary={
                      `Astreintes: ${dist.assignmentsBefore} + ${dist.assignmentsAdded} = ${dist.assignmentsTotal} • ` +
                      `Primes: ${dist.primesBefore}€ + ${dist.primesAdded}€ = ${dist.primesTotal}€`
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Performances de l'algorithme */}
        {planning.processingTime && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>⚡ Performances & Configuration IA</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {/* Modèle utilisé */}
                {planning.metadata?.model && (
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    🤖 <strong>Modèle utilisé :</strong> {planning.metadata.modelDisplayName || planning.metadata.model}
                  </Typography>
                )}
                
                {/* Provider */}
                {planning.metadata?.aiProvider && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    🌐 <strong>Provider :</strong> {planning.metadata.aiProvider}
                  </Typography>
                )}
                
                {/* Temps de traitement */}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ⏱️ <strong>Temps total de traitement :</strong> {planning.processingTime.formattedDuration}
                </Typography>
                
                {/* Tokens utilisés */}
                {planning.metadata?.tokensUsed && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    💬 <strong>Tokens utilisés :</strong> {planning.metadata.tokensUsed.toLocaleString()}
                    {planning.metadata.promptTokens && planning.metadata.completionTokens && (
                      <span style={{ color: '#666', fontSize: '0.9em' }}>
                        {' '}(prompt: {planning.metadata.promptTokens.toLocaleString()}, réponse: {planning.metadata.completionTokens.toLocaleString()})
                      </span>
                    )}
                  </Typography>
                )}
                
                {/* Configuration */}
                {planning.metadata?.temperature !== undefined && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    🌡️ <strong>Température :</strong> {planning.metadata.temperature}
                  </Typography>
                )}
                
                {planning.metadata?.maxTokens && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    📊 <strong>Tokens max :</strong> {planning.metadata.maxTokens.toLocaleString()}
                  </Typography>
                )}
                
                {/* Itérations (pour algorithmes non-IA) */}
                {planning.processingTime.iterations > 0 && (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      🔄 <strong>Nombre d'itérations :</strong> {planning.processingTime.iterations}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      📈 <strong>Temps par itération :</strong> {Math.round(planning.processingTime.totalMs / planning.processingTime.iterations)}ms en moyenne
                    </Typography>
                  </>
                )}
                
                {/* Score */}
                {planning.score !== undefined && planning.score !== null && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ⭐ <strong>Score final :</strong> {planning.score.toFixed(2)}
                  </Typography>
                )}
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  {planning.metadata?.model?.includes('claude-opus') 
                    ? '🧠 Claude Opus 4.5 offre un raisonnement logique exceptionnel et une gestion optimale des contraintes complexes.'
                    : planning.metadata?.model?.includes('gpt-5.1')
                    ? '🚀 GPT-5.1 combine rapidité et qualité pour des planifications efficaces.'
                    : 'L\'algorithme utilise une approche intelligente en 3 phases pour optimiser la couverture des compétences tout en équilibrant la charge de travail entre les techniciens.'
                  }
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    );
  };

  const renderCompletedStep = () => (
    <Box textAlign="center" py={3}>
      <Typography variant="h6" color="success.main" gutterBottom>
        Planning appliqué avec succès !
      </Typography>
      <Typography variant="body1">
        {planning?.assignments.length} astreintes ont été créées et sont maintenant visibles dans le calendrier.
      </Typography>
    </Box>
  );

  const renderGeneratingStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Génération du planning en cours...
      </Typography>

      {/* Stepper principal */}
      <Stepper activeStep={progressInfo.currentStep} orientation="vertical" sx={{ mb: 3 }}>
        {progressInfo.steps.map((stepInfo, index) => (
          <Step key={stepInfo.id}>
            <StepLabel 
              icon={progressInfo.currentStep >= index ? <CheckCircleIcon color="primary" /> : stepInfo.icon}
              sx={{
                '& .MuiStepLabel-label': {
                  fontWeight: progressInfo.currentStep === index ? 'bold' : 'normal',
                  color: progressInfo.currentStep >= index ? 'primary.main' : 'text.secondary'
                }
              }}
            >
              {stepInfo.label}
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                {stepInfo.description}
              </Typography>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {/* Message de progression actuel */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'background.default' }}>
        <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
          {progressInfo.currentMessage}
        </Typography>
        
        {/* Barre de progression des itérations */}
        {progressInfo.currentIteration > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Itération {progressInfo.currentIteration} / {progressInfo.maxIterations}
              </Typography>
              {progressInfo.startTime && (
                <Typography variant="caption" color="text.secondary">
                  Temps écoulé: {formatElapsedTime(progressInfo.startTime)}
                </Typography>
              )}
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(100, (progressInfo.currentIteration / progressInfo.maxIterations) * 100)}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}
      </Paper>

      {/* Informations additionnelles */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          L'algorithme intelligent analyse <strong>{formData.selectedTechnicians.length} techniciens</strong> et{' '}
          <strong>{formData.selectedSkills.length} compétences</strong> pour optimiser la répartition des astreintes.
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
          💡 Patience, l'algorithme explore différentes stratégies pour trouver la solution optimale !
        </Typography>
      </Alert>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon color="primary" />
          <span>Planification IA</span>
        </Box>
        {loading && <LinearProgress sx={{ mt: 1 }} />}
      </DialogTitle>

      <DialogContent>
        {/* Layout Grid : 2/3 Contenu + 1/3 Chat */}
        <Grid container spacing={2}>
          {/* Colonne principale : Contenu du dialogue */}
          <Grid item xs={12} md={formData.aiProvider === 'multi-agents' ? 8 : 12}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {step === 'setup' && renderSetupStep()}
            {step === 'generating' && renderGeneratingStep()}
            {step === 'preview' && renderPreviewStep()}
            {step === 'completed' && renderCompletedStep()}
          </Grid>
          
          {/* Colonne secondaire : Chat en temps réel (1/3) */}
          {/* ✅ Toujours visible en mode Hybride (qui est le mode par défaut) */}
          <Grid 
            item 
            xs={12} 
            md={4} 
            sx={{ 
              display: open ? 'block' : 'none' // Toujours visible quand dialogue ouvert
            }}
          >
            <AgentDialogueChat
              isActive={open}
              isVisible={open}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {step === 'setup' && (
          <>
            <Button onClick={handleClose}>Annuler</Button>
            <Button 
              onClick={generatePlanning} 
              variant="contained"
              disabled={loading}
            >
              Générer le planning
            </Button>
          </>
        )}
        {step === 'preview' && (
          <>
            <Button onClick={() => setStep('setup')}>Retour</Button>
            <Button 
              onClick={applyPlanning} 
              variant="contained"
              disabled={loading || planning?.conflicts.length > 0}
            >
              Appliquer le planning
            </Button>
          </>
        )}
        {step === 'completed' && (
          <Button onClick={handleClose} variant="contained">
            Fermer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AutoPlanningDialog; 